import { usesPriceAdjustment } from '../../../shared/pricingDisplay';
import MarginField from '../../components/MarginField';
import { marginDisplay } from '../../../shared/pricingDisplay';
/**
 * WooSync - Supabase → WooCommerce Sync Management
 *
 * Koristi Edge Function za sinhronizaciju proizvoda.
 * Čita i editira konfiguraciju iz woo_shops tabele.
 */

import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Checkbox,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  ListItemButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Sync as SyncIcon,
  Store as StoreIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  History as HistoryIcon,
  CloudSync as CloudSyncIcon,
  Category as CategoryIcon,
  AttachMoney as PriceIcon,
  FilterList as FilterIcon,
  ListAlt as FieldsIcon,
  Save as SaveIcon,
  ArrowForward as ArrowIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  LinkOff as UnmappedIcon,
  Link as MappedIcon,
  TextFields as TextIcon,
  Visibility as PreviewIcon,
  Casino as RandomIcon,
  Storage as DatabaseIcon,
  ShoppingCart as WooIcon,
  Bolt as TriggerIcon,
  Queue as QueueIcon,
  Cancel as CancelIcon,
  RocketLaunch as PluginIcon,
  Article as LogIcon,
  Star as PremiumIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import styles from './WooSync.module.css';
import { supabase, WooShop, SupplierFilter } from '../../lib/supabase';
import { adminApi } from '../../lib/adminApi';
import { NameRulesConfig, DEFAULT_NAME_RULES_CONFIG } from '../../types/shop';
import NameRulesPanel from '../../components/SyncProfileEditor/NameRulesPanel';
import {
  buildProductData,
  syncSingleProduct,
  findTargetProduct,
  SyncConfig,
  WooTargetProduct,
} from '../../services/syncProductService';

// ============================================================================
// TYPES
// ============================================================================

interface SyncLog {
  id: number;
  shop_id: string;
  shop_name: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  products_created: number;
  products_updated: number;
  products_skipped: number;
  products_failed: number;
  error_message: string | null;
  details: any;
}

interface ShopWithLogs extends WooShop {
  recentLogs?: SyncLog[];
  isExpanded?: boolean;
}

interface ActiveSessionInfo {
  id: string;
  status: string;
  progress: number;
  to_create: number;
  to_update: number;
  to_delete: number;
  current_offset: number;
  total_tasks: number;
  created: number;
  updated: number;
  deleted: number;
  errors: number;
  created_at: string;
}

// Sync fields that can be toggled
const SYNC_FIELD_OPTIONS = [
  { key: 'name', label: 'Naziv', category: 'Osnovni podaci' },
  { key: 'sku', label: 'SKU', category: 'Osnovni podaci' },
  { key: 'status', label: 'Status', category: 'Osnovni podaci' },
  { key: 'regular_price', label: 'Redovna cijena', category: 'Cijene' },
  { key: 'sale_price', label: 'Akcijska cijena', category: 'Cijene' },
  { key: 'stock_quantity', label: 'Količina na stanju', category: 'Zalihe' },
  { key: 'stock_status', label: 'Status zalihe', category: 'Zalihe' },
  { key: 'manage_stock', label: 'Upravljanje zalihama', category: 'Zalihe' },
  { key: 'description', label: 'Opis', category: 'Opisi' },
  { key: 'short_description', label: 'Kratki opis', category: 'Opisi' },
  { key: 'categories', label: 'Kategorije', category: 'Taksonomije' },
  { key: 'tags', label: 'Tagovi', category: 'Taksonomije' },
  { key: 'attributes', label: 'Atributi (pa_brend, pa_garancija)', category: 'Taksonomije' },
  { key: 'images', label: 'Slike', category: 'Mediji' },
  { key: 'weight', label: 'Težina', category: 'Dimenzije' },
  { key: 'dimensions', label: 'Dimenzije (DxŠxV)', category: 'Dimenzije' },
];

// Polja koja se mogu izuzeti iz sinhronizacije po kategoriji (category_field_exclusions).
// Redovna cijena povlači i akcijsku, zalihe povlače i status — vidi getCategoryExcludedFields
// u api/lib/syncProductLogic.ts (build gnijezdi zavisna polja).
const CATEGORY_EXCLUDABLE_FIELDS: { key: string; label: string }[] = [
  { key: 'regular_price', label: 'Redovna cijena (+akcijska)' },
  { key: 'sale_price', label: 'Akcijska cijena' },
  { key: 'stock_quantity', label: 'Zalihe (količina + status)' },
  { key: 'name', label: 'Naziv' },
  { key: 'description', label: 'Opisi' },
  { key: 'images', label: 'Slike' },
  { key: 'categories', label: 'Kategorije' },
  { key: 'attributes', label: 'Atributi' },
];

// Available Supabase product columns for Custom Field mapping
const SUPABASE_PRODUCT_FIELDS = [
  { key: 'sku', label: 'SKU' },
  { key: 'name', label: 'Naziv' },
  { key: 'slug', label: 'Slug' },
  { key: 'global_unique_id', label: 'Global Unique ID' },
  { key: 'status', label: 'Status' },
  { key: 'type', label: 'Tip' },
  { key: 'featured', label: 'Istaknuto' },
  { key: 'catalog_visibility', label: 'Vidljivost u katalogu' },
  { key: 'regular_price', label: 'Redovna cijena' },
  { key: 'sale_price', label: 'Akcijska cijena' },
  { key: 'nabavna_cijena', label: 'Nabavna cijena' },
  { key: 'stock_quantity', label: 'Količina na stanju' },
  { key: 'stock_status', label: 'Status zalihe' },
  { key: 'manage_stock', label: 'Upravljanje zalihama' },
  { key: 'backorders', label: 'Backorders' },
  { key: 'weight', label: 'Težina' },
  { key: 'length', label: 'Dužina' },
  { key: 'width', label: 'Širina' },
  { key: 'height', label: 'Visina' },
  { key: 'shipping_class', label: 'Klasa dostave' },
  { key: 'description', label: 'Opis' },
  { key: 'short_description', label: 'Kratki opis' },
  { key: 'pdv', label: 'PDV' },
  { key: 'garancija', label: 'Garancija' },
  { key: 'dobavljac', label: 'Dobavljač' },
  { key: 'ean', label: 'EAN kod' },
  { key: 'brand', label: 'Brend' },
  { key: 'categories', label: 'Kategorije' },
  { key: 'feed_source', label: 'Feed izvor' },
  { key: 'supplier_stocks', label: 'Zalihe po dobavljačima (JSON)' },
  { key: 'stock_distant', label: 'Zalihe stranih dobavljača' },
  { key: 'delivery_days', label: 'Rok isporuke (dana)' },
  { key: 'delivery_days_min', label: 'Rok isporuke min (samo distant)' },
  { key: 'available_suppliers', label: 'Dostupni dobavljači' },
  { key: 'import_batch_id', label: 'Import Batch ID' },
  { key: 'created_at', label: 'Datum kreiranja' },
  { key: 'updated_at', label: 'Datum ažuriranja' },
];

// ============================================================================
// SUPPLIER CATEGORIES FILTER - multi-select source kategorija po dobavljaču
// ============================================================================

function SupplierCategoriesFilter({
  shopId,
  supplier,
  value,
  onChange,
  placeholder,
  chipColor,
}: {
  shopId: string | undefined;
  supplier?: string; // bez dobavljača = sve source kategorije shopa
  value: string[];
  onChange: (categories: string[]) => void;
  placeholder?: string;
  chipColor?: 'primary' | 'error';
}) {
  const [options, setOptions] = useState<{ name: string; count: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadOptions = async () => {
    if (loaded || loading || !shopId) return;
    setLoading(true);
    try {
      const url = `/api/supabase-categories?shopId=${shopId}${supplier ? `&supplier=${encodeURIComponent(supplier)}` : ''}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setOptions((data.categories || []).map((c: any) => ({ name: c.name, count: c.count })));
        setLoaded(true);
      } else {
        toast.error(`Greška pri učitavanju kategorija${supplier ? ` za ${supplier}` : ''}`);
      }
    } catch (err) {
      console.error(`Error loading categories${supplier ? ` for ${supplier}` : ''}:`, err);
      toast.error(`Greška pri učitavanju kategorija${supplier ? ` za ${supplier}` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={options.map(o => o.name)}
      value={value}
      onOpen={loadOptions}
      onChange={(_, newValue) => onChange(newValue as string[])}
      loading={loading}
      renderOption={(props, option) => {
        const opt = options.find(o => o.name === option);
        return (
          <li {...props} key={option}>
            <Typography variant="body2" sx={{ flex: 1 }}>{option}</Typography>
            {opt && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {opt.count}
              </Typography>
            )}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={option}
            size="small"
            color={chipColor ?? 'primary'}
            variant="outlined"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? (placeholder ?? 'Sve kategorije') : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ minWidth: 220 }}
    />
  );
}

// ============================================================================
// SUPPLIER CATEGORY BRANDS - po odabranoj kategoriji bira se koji brendovi ulaze
// Prazno = svi brendovi te kategorije. Kategorije bez unosa se ne diraju.
// ============================================================================

function SupplierCategoryBrands({
  supplier,
  categories,
  value,
  onChange,
}: {
  supplier: string;
  categories: string[];
  value: Record<string, string[]>;
  onChange: (next: Record<string, string[]>) => void;
}) {
  const [optionsByCategory, setOptionsByCategory] = useState<Record<string, { name: string; count: number }[]>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const loadBrands = async (category: string) => {
    if (optionsByCategory[category] || loading[category]) return;
    setLoading(prev => ({ ...prev, [category]: true }));
    try {
      const response = await fetch(
        `/api/supabase-brands?supplier=${encodeURIComponent(supplier)}&category=${encodeURIComponent(category)}`
      );
      if (response.ok) {
        const data = await response.json();
        setOptionsByCategory(prev => ({ ...prev, [category]: data.brands || [] }));
      } else {
        toast.error(`Greška pri učitavanju brendova za ${category}`);
      }
    } catch (err) {
      console.error(`Error loading brands for ${supplier}/${category}:`, err);
      toast.error(`Greška pri učitavanju brendova za ${category}`);
    } finally {
      setLoading(prev => ({ ...prev, [category]: false }));
    }
  };

  if (categories.length === 0) return null;

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {categories.map(category => {
        const selected = value[category] || [];
        const options = optionsByCategory[category] || [];
        return (
          <Box key={category} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              title={category}
              sx={{ minWidth: 110, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {category}
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={options.map(o => o.name)}
              value={selected}
              onOpen={() => loadBrands(category)}
              onChange={(_, newValue) => {
                const next = { ...value };
                if ((newValue as string[]).length === 0) delete next[category];
                else next[category] = newValue as string[];
                onChange(next);
              }}
              loading={!!loading[category]}
              renderOption={(props, option) => {
                const opt = options.find(o => o.name === option);
                return (
                  <li {...props} key={option}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{option}</Typography>
                    {opt && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {opt.count}
                      </Typography>
                    )}
                  </li>
                );
              }}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selected.length === 0 ? 'Svi brendovi' : ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading[category] ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ flex: 1, minWidth: 180 }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function WooSync() {
  const muiTheme = useTheme();
  const fullScreenDialog = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [shops, setShops] = useState<ShopWithLogs[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  // Shop list search/filter state
  const [shopSearch, setShopSearch] = useState('');
  const [shopFilter, setShopFilter] = useState<'all' | 'active' | 'inactive' | 'errors'>('all');

  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState<{ el: HTMLElement; shopId: string } | null>(null);

  // Sync dialog state
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncingShop, setSyncingShop] = useState<ShopWithLogs | null>(null);
  const [syncProgress, setSyncProgress] = useState<any>(null);
  const [syncResult, setSyncResult] = useState<any>(null);

  // Settings dialog state
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingShop, setEditingShop] = useState<ShopWithLogs | null>(null);
  const [settingsTab, setSettingsTab] = useState(0);
  const [saving, setSaving] = useState(false);

  // Editable settings
  const [syncFields, setSyncFields] = useState<Record<string, boolean>>({});
  const [pricingConfig, setPricingConfig] = useState<any>({});
  const [sourceFilters, setSourceFilters] = useState<any>({});
  const [syncOptions, setSyncOptions] = useState<{
    sync_mode?: 'create_and_update' | 'create_only' | 'update_only' | 'delete_only' | 'create_and_update_managed';
    new_product_status?: 'draft' | 'publish';
    attributes_mode?: 'smart' | 'replace_all';
    excluded_attributes?: string[];
    attribute_name_map?: Record<string, string>;
    delete_action?: 'none' | 'draft' | 'trash';
    delete_products?: boolean;
    supplier_menu_order?: {
      enabled: boolean;
      orders: Record<string, number>;
      default_order: number;
    };
    supplier_delete_actions?: Array<{
      supplier: string;
      delete_action: 'draft' | 'permanent_delete';
    }>;
  }>({});
  const [nameRulesConfig, setNameRulesConfig] = useState<NameRulesConfig>(DEFAULT_NAME_RULES_CONFIG);

  // Available suppliers (for autocomplete)
  const [availableSuppliers, setAvailableSuppliers] = useState<string[]>([]);

  // MPC override: novi mpcOverride ključ ima prednost, stari doperMpcOverride se čita
  // kao prioritetan DOPER pa postojeći shopovi rade bez ijedne izmjene u bazi.
  // String element u listi = prioritetan dobavljač (prva verzija ključa).
  const mpcOverrideCfg = pricingConfig?.mpcOverride;
  const mpcOverrideEnabled = mpcOverrideCfg
    ? mpcOverrideCfg.enabled === true
    : pricingConfig?.doperMpcOverride?.enabled === true;
  const mpcOverrideList: { name: string; onlyWhenSole: boolean }[] = (
    mpcOverrideCfg
      ? (Array.isArray(mpcOverrideCfg.suppliers) ? mpcOverrideCfg.suppliers : [])
      : (pricingConfig?.doperMpcOverride?.enabled === true ? ['DOPER'] : [])
  )
    .map((s: any) => typeof s === 'string'
      ? { name: s, onlyWhenSole: false }
      : { name: String(s?.name ?? ''), onlyWhenSole: s?.onlyWhenSole === true })
    .filter((s: { name: string }) => s.name.length > 0);
  // Svaka izmjena piše samo novi ključ i briše stari — jedan izvor istine
  const setMpcOverride = (
    enabled: boolean,
    suppliers: { name: string; onlyWhenSole: boolean }[]
  ) =>
    setPricingConfig((prev: any) => {
      const { doperMpcOverride: _legacy, ...rest } = prev || {};
      return { ...rest, mpcOverride: { enabled, suppliers } };
    });

  // Categories tab state
  const [supabaseCategories, setSupabaseCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [mappingSearch, setMappingSearch] = useState('');
  const [newMapping, setNewMapping] = useState<{ sourceValue: string; targetId: string; targetName: string }>({
    sourceValue: '',
    targetId: '',
    targetName: '',
  });
  const [editingMapping, setEditingMapping] = useState<string | null>(null); // source value being edited
  const [showOnlyUnmapped, setShowOnlyUnmapped] = useState(false);

  // Attributes tab state
  const [attributeMappingJson, setAttributeMappingJson] = useState('');
  const [attributeMappingError, setAttributeMappingError] = useState<string | null>(null);
  const [supabaseAttributes, setSupabaseAttributes] = useState<string[]>([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [attributeSearch, setAttributeSearch] = useState('');
  const [selectedAttribute, setSelectedAttribute] = useState<string | null>(null);
  const [targetAttributeName, setTargetAttributeName] = useState('');

  // Glavni atributi (za sekciju isključivanja/mapiranja po shopu)
  const [mainAttributes, setMainAttributes] = useState<Array<{ slug: string; label: string }>>([]);

  // Custom Field Mappings tab state
  const [customFieldMappings, setCustomFieldMappings] = useState<{
    sourceField: string;
    targetField: string;
    applyTo: 'all' | 'new' | 'existing';
    enabled: boolean;
  }[]>([]);

  // Supabase Trigger tab state - JSONB objekt koji prati koja polja triggeraju sync
  const [webhookTriggerFields, setWebhookTriggerFields] = useState<Record<string, boolean>>({});
  const [pluginConfig, setPluginConfig] = useState<{ enabled: boolean; api_key: string }>({ enabled: false, api_key: '' });
  const [premiumFeatures, setPremiumFeatures] = useState<{ enable_image_search: boolean; enable_llm: boolean; enable_bulk_campaigns: boolean; enable_olx: boolean; enable_omnibus: boolean; enable_supabase_direct: boolean; enable_review_emails: boolean; enable_olx_ai_replies: boolean }>({ enable_image_search: false, enable_llm: false, enable_bulk_campaigns: false, enable_olx: false, enable_omnibus: false, enable_supabase_direct: false, enable_review_emails: false, enable_olx_ai_replies: false });
  const [syncQueueStats, setSyncQueueStats] = useState<{ pending: number; processing: number; done: number; error: number } | null>(null);
  const [loadingQueueStats, setLoadingQueueStats] = useState(false);

  // Sync Log state
  const [syncLogDialogOpen, setSyncLogDialogOpen] = useState(false);
  const [syncLogData, setSyncLogData] = useState<{
    batch_id: string;
    batch_status: string;
    batch_created_at: string;
    stats: { total: number; success: number; failed: number; created: number; updated: number; deleted: number };
    logs: Array<{
      sku: string;
      product_id: number | null;
      action: string;
      changes: Record<string, any> | null;
      status: string;
      error_message: string | null;
    }>;
  } | null>(null);
  const [loadingSyncLog, setLoadingSyncLog] = useState(false);
  const [syncLogFilter, setSyncLogFilter] = useState<'all' | 'success' | 'failed'>('all');

  // Live Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewShop, setPreviewShop] = useState<ShopWithLogs | null>(null);
  const [previewProduct, setPreviewProduct] = useState<any>(null);
  const [previewMappedProduct, setPreviewMappedProduct] = useState<any>(null);
  const [previewTargetProduct, setPreviewTargetProduct] = useState<WooTargetProduct | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [syncingPreview, setSyncingPreview] = useState(false); // Za sync button
  const [previewSkuInput, setPreviewSkuInput] = useState(''); // SKU input za preview

  // Active session state - prati aktivne sync sesije po shopu
  const [activeSessions, setActiveSessions] = useState<Record<string, ActiveSessionInfo | null>>({});
  const [cancelingSession, setCancelingSession] = useState<Record<string, boolean>>({});

  // Add/Delete shop state
  const [addShopDialogOpen, setAddShopDialogOpen] = useState(false);
  const [deleteShopDialogOpen, setDeleteShopDialogOpen] = useState(false);
  const [shopToDelete, setShopToDelete] = useState<ShopWithLogs | null>(null);
  const [newShopData, setNewShopData] = useState({
    name: '',
    url: '',
    consumer_key: '',
    consumer_secret: '',
    plugin_enabled: false,
    plugin_api_key: '',
  });
  const [addingShop, setAddingShop] = useState(false);
  const [deletingShop, setDeletingShop] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // =========================================================================
  // DATA LOADING
  // =========================================================================

  const loadShops = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Dohvati shopove preko admin API-ja (Firebase auth + service_role server-side)
      let shopsData: any[];
      try {
        shopsData = await adminApi.shops.list();
      } catch (err: any) {
        throw new Error(`Failed to load shops: ${err?.message || err}`);
      }

      // Dohvati zadnje logove za svaki shop (preko admin API-ja)
      const shopsWithLogs: ShopWithLogs[] = await Promise.all(
        (shopsData || []).map(async (shop) => {
          let logs: any[] = [];
          try {
            logs = await adminApi.syncLogs.list(5, shop.id);
          } catch {
            logs = [];
          }
          return {
            ...shop,
            recentLogs: logs,
            isExpanded: false,
          };
        })
      );

      setShops(shopsWithLogs);

      // Dohvati dostupne dobavljače preko admin API-ja
      let suppliersData: Array<{ name: string }> = [];
      try {
        const all = await adminApi.suppliers.list();
        suppliersData = (all || []).map((s: any) => ({ name: s.name }));
      } catch (err) {
        console.error('Error loading suppliers:', err);
      }

      const supplierNames = (suppliersData || []).map(s => s.name).filter(Boolean);
      console.log('Available suppliers loaded:', supplierNames);
      setAvailableSuppliers(supplierNames as string[]);

    } catch (err: any) {
      console.error('Error loading shops:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  // Učitaj listu glavnih atributa jednom (za checklist isključivanja)
  useEffect(() => {
    adminApi.mainAttributes
      .list()
      .then((res) => setMainAttributes(res.attributes || []))
      .catch((err) => console.error('Greška pri učitavanju glavnih atributa:', err));
  }, []);

  // =========================================================================
  // SESSION MANAGEMENT - provjera i cancel aktivnih sesija
  // =========================================================================

  const checkActiveSession = useCallback(async (shopId: string) => {
    try {
      const response = await fetch(`/api/sync/sessions?shopId=${shopId}`);
      if (!response.ok) return null;

      const data = await response.json();
      if (data.hasActiveSession && data.session) {
        return data.session as ActiveSessionInfo;
      }
      return null;
    } catch (err) {
      console.error('Error checking session:', err);
      return null;
    }
  }, []);

  const cancelActiveSession = useCallback(async (shopId: string) => {
    setCancelingSession(prev => ({ ...prev, [shopId]: true }));
    try {
      const response = await fetch(`/api/sync/sessions?shopId=${shopId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel session');
      }

      const result = await response.json();
      toast.success(result.message || 'Sesija prekinuta');

      // Očisti session state
      setActiveSessions(prev => ({ ...prev, [shopId]: null }));
      setSyncing(prev => ({ ...prev, [shopId]: false }));

    } catch (err: any) {
      console.error('Error canceling session:', err);
      toast.error(`Greška: ${err.message}`);
    } finally {
      setCancelingSession(prev => ({ ...prev, [shopId]: false }));
    }
  }, []);

  // Ručna provjera sesije za pojedinačni shop (na klik)
  const [checkingSession, setCheckingSession] = useState<Record<string, boolean>>({});

  const checkAndShowSession = useCallback(async (shopId: string) => {
    setCheckingSession(prev => ({ ...prev, [shopId]: true }));
    try {
      const session = await checkActiveSession(shopId);
      setActiveSessions(prev => ({ ...prev, [shopId]: session }));
      if (!session) {
        toast.info('Nema aktivne sesije');
      }
    } finally {
      setCheckingSession(prev => ({ ...prev, [shopId]: false }));
    }
  }, [checkActiveSession]);

  // =========================================================================
  // SETTINGS FUNCTIONS
  // =========================================================================

  // Učitaj kategorije za specifični shop (filtrirano po dobavljačima)
  const loadSupabaseCategories = useCallback(async (shopId?: string) => {
    setLoadingCategories(true);
    try {
      // Ako imamo shopId, koristi novi API koji filtrira po dobavljačima
      if (shopId) {
        try {
          const response = await fetch(`/api/supabase-categories?shopId=${shopId}`);
          const contentType = response.headers.get('content-type') || '';
          if (response.ok && contentType.includes('application/json')) {
            const data = await response.json();
            // API vraća kategorije sa isMapped statusom, izvuci samo imena
            const categories = (data.categories || [])
              .map((cat: any) => cat.name)
              .filter(Boolean)
              .sort((a: string, b: string) => a.localeCompare(b, 'hr'));
            setSupabaseCategories(categories);
            console.log(`Učitano ${categories.length} kategorija za shop ${data.shopName} (filtrirano po dobavljačima: ${data.allowedSuppliers?.join(', ')})`);
            return;
          }
        } catch (apiErr) {
          console.warn('API endpoint not available, using Supabase fallback');
        }
      }

      // Direct aggregation through the admin API (RPC path removed since the
      // anon key no longer has access to the products table).
      {
        const products = (await adminApi.products.list({
          fields: ['categories'],
          has_categories: true,
          limit: 10000,
        })) as any[];

        const allCategories = new Set<string>();
        (products || []).forEach((product: any) => {
          const cats = product.categories;
          if (!cats) return;

          if (typeof cats === 'string') {
            const parts = cats.split(/[|>]/).map((s: string) => s.trim()).filter(Boolean);
            parts.forEach((cat: string) => allCategories.add(cat));
          } else if (Array.isArray(cats)) {
            cats.forEach((cat: any) => {
              if (typeof cat === 'string') {
                allCategories.add(cat.trim());
              } else if (cat?.name) {
                allCategories.add(cat.name.trim());
              }
            });
          }
        });

        const sortedCategories = Array.from(allCategories).sort((a, b) =>
          a.localeCompare(b, 'hr')
        );
        setSupabaseCategories(sortedCategories);
      }
    } catch (err: any) {
      console.error('Error loading categories:', err);
      toast.error(`Greška pri učitavanju kategorija: ${err.message}`);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const openSettings = (shop: ShopWithLogs) => {
    setEditingShop(shop);
    // Inicijaliziraj SVA sync polja - default je true ako nije definirano u bazi
    const defaultSyncFields = SYNC_FIELD_OPTIONS.reduce((acc, field) => {
      acc[field.key] = shop.sync_fields?.[field.key] ?? true;
      return acc;
    }, {} as Record<string, boolean>);
    setSyncFields(defaultSyncFields);
    setPricingConfig(shop.pricing_config || {});
    setSourceFilters(shop.source_filters || {});
    setSyncOptions((shop as any).sync_options || {});
    setNameRulesConfig((shop as any).name_rules_config || DEFAULT_NAME_RULES_CONFIG);
    setCustomFieldMappings((shop as any).custom_field_mappings || []);
    setWebhookTriggerFields((shop as any).webhook_trigger_fields || {});
    setPluginConfig((shop as any).plugin_config || { enabled: false, api_key: '' });
    setPremiumFeatures({
      enable_image_search: (shop as any).enable_image_search === true,
      enable_llm: (shop as any).enable_llm === true,
      enable_bulk_campaigns: (shop as any).enable_bulk_campaigns === true,
      enable_olx: (shop as any).enable_olx === true,
      enable_omnibus: (shop as any).enable_omnibus === true,
      enable_supabase_direct: (shop as any).enable_supabase_direct === true,
      enable_review_emails: shop.enable_review_emails === true,
      enable_olx_ai_replies: shop.enable_olx_ai_replies === true
    });
    setSettingsTab(0);
    setCategorySearch('');
    setNewMapping({ sourceValue: '', targetId: '', targetName: '' });
    setShowOnlyUnmapped(false);
    setSettingsDialogOpen(true);
    // Učitaj kategorije za ovaj shop (filtrirano po dobavljačima)
    // Uvijek ponovno učitaj jer svaki shop ima druge dobavljače
    loadSupabaseCategories(shop.id);
    // Učitaj statistike queue-a za ovaj shop
    loadSyncQueueStats(shop.id!);
  };

  const loadSyncQueueStats = async (shopId: string) => {
    setLoadingQueueStats(true);
    try {
      // Aggregated on the server via admin API (needs_sync / last_synced_at).
      const agg = await adminApi.productSyncStatus.stats(shopId);
      const stats = {
        pending: agg.pending || 0,
        processing: agg.processing || 0,
        done: agg.done || 0,
        error: 0, // Ne pratimo greške u ovom sustavu
      };
      setSyncQueueStats(stats);
    } catch (err) {
      console.error('Error loading queue stats:', err);
      setSyncQueueStats(null);
    } finally {
      setLoadingQueueStats(false);
    }
  };

  const saveSettings = async () => {
    if (!editingShop) return;

    setSaving(true);
    try {
      await adminApi.shops.update(editingShop.id, {
        sync_fields: syncFields,
        pricing_config: pricingConfig,
        source_filters: sourceFilters,
        sync_options: syncOptions,
        name_rules_config: nameRulesConfig,
        custom_field_mappings: customFieldMappings,
        webhook_trigger_fields: webhookTriggerFields,
        plugin_config: pluginConfig.enabled && pluginConfig.api_key
          ? { enabled: true, api_key: pluginConfig.api_key }
          : null,
        enable_image_search: premiumFeatures.enable_image_search,
        enable_llm: premiumFeatures.enable_llm,
        enable_bulk_campaigns: premiumFeatures.enable_bulk_campaigns,
        enable_olx: premiumFeatures.enable_olx,
        enable_omnibus: premiumFeatures.enable_omnibus,
        enable_supabase_direct: premiumFeatures.enable_supabase_direct,
        enable_review_emails: premiumFeatures.enable_review_emails,
        enable_olx_ai_replies: premiumFeatures.enable_olx_ai_replies,
      });

      toast.success('Postavke sačuvane!');
      setSettingsDialogOpen(false);
      await loadShops();
    } catch (err: any) {
      toast.error(`Greška: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // =========================================================================
  // SYNC FUNCTIONS
  // =========================================================================

  const CRON_SECRET_STORAGE_KEY = 'sejkansync_cron_secret';

  const getCronSecret = (): string => {
    const existing = localStorage.getItem(CRON_SECRET_STORAGE_KEY);
    if (existing && existing.trim()) return existing.trim();

    const input = window.prompt('Unesi CRON_SECRET (čuva se lokalno u browseru):');
    if (!input || !input.trim()) {
      throw new Error('CRON_SECRET nije unesen');
    }
    localStorage.setItem(CRON_SECRET_STORAGE_KEY, input.trim());
    return input.trim();
  };

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Fetch sync log from plugin
  const fetchSyncLog = async (shop: ShopWithLogs) => {
    if (!shop.id) return;

    setLoadingSyncLog(true);
    setSyncLogDialogOpen(true);
    setSyncLogData(null);

    try {
      const secret = getCronSecret();
      const response = await fetch(
        `/api/sync/sync-log?shopId=${shop.id}&secret=${secret}&status=${syncLogFilter}`
      );

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to fetch sync log');
      }

      const data = await response.json();
      setSyncLogData(data);
    } catch (err: any) {
      toast.error(`Greška: ${err.message}`);
      setSyncLogDialogOpen(false);
    } finally {
      setLoadingSyncLog(false);
    }
  };



  /**
   * FULL SYNC - Kompletna sinhronizacija sa DELETE logikom
   * Podržava CHUNKED PROCESSING za izbjegavanje timeout-a
   */
  const triggerFullSync = async (shop: ShopWithLogs, options?: { dryRun?: boolean; deleteAction?: string }) => {
    setSyncingShop(shop);
    setSyncDialogOpen(true);
    setSyncProgress({ status: 'starting', message: 'Pokrećem FULL SYNC...' });
    setSyncResult(null);
    setSyncing(prev => ({ ...prev, [shop.id!]: true }));

    // Akumulirani rezultati kroz sve chunk-ove
    const accumulatedStats = {
      created: 0,
      updated: 0,
      skipped: 0,
      deleted: 0,
      errors: 0,
    };
    const allLogs: Array<{ level: string; message: string }> = [];

    try {
      const cronSecret = getCronSecret();
      const dryRun = options?.dryRun === true;
      const deleteAction = options?.deleteAction || 'draft';
      const chunkSize = 100; // Procesira 100 proizvoda po chunk-u
      const importKey = crypto.randomUUID();

      let currentSessionId: string | null = null;
      let hasMore = true;
      let totalTasks = 0;
      let chunkNumber = 0;

      // Chunk processing loop sa auto-retry
      const MAX_RETRIES = 3;
      let consecutiveErrors = 0;

      while (hasMore) {
        chunkNumber++;
        const params = new URLSearchParams({
          shopId: shop.id!,
          deleteAction,
          chunkSize: chunkSize.toString(),
        });
        if (dryRun) params.set('dryRun', 'true');
        if (currentSessionId) params.set('sessionId', currentSessionId);

        const currentOffset = (chunkNumber - 1) * chunkSize;
        const progressMsg = totalTasks > 0
          ? `Chunk ${chunkNumber}: Procesiram ${currentOffset + 1}-${Math.min(currentOffset + chunkSize, totalTasks)} od ${totalTasks}...`
          : 'Analiziram proizvode...';
        setSyncProgress({ status: 'running', message: progressMsg });

        const url = `/api/sync/full-sync?${params.toString()}`;

        let response;
        let data;
        let retryCount = 0;

        // Retry loop za timeout/network errors
        while (retryCount < MAX_RETRIES) {
          try {
            response = await fetch(url, {
              method: 'GET',
              headers: { 'Authorization': `Bearer ${cronSecret}`, 'Idempotency-Key': importKey },
            });

            data = await response.json().catch(() => null);

            if (response.ok) {
              consecutiveErrors = 0;
              break; // Success, exit retry loop
            }

            // Server error (5xx) - retry
            if (response.status >= 500) {
              retryCount++;
              if (retryCount < MAX_RETRIES) {
                const waitTime = retryCount * 2000;
                setSyncProgress({
                  status: 'running',
                  message: `Server error ${response.status}, retry ${retryCount}/${MAX_RETRIES} za ${waitTime/1000}s...`
                });
                await new Promise(r => setTimeout(r, waitTime));
                continue;
              }
            }

            // Client error (4xx) - don't retry
            throw new Error(data?.error || `HTTP ${response.status}`);

          } catch (fetchError: any) {
            // Network error (timeout, connection refused, etc.)
            retryCount++;
            if (retryCount < MAX_RETRIES) {
              const waitTime = retryCount * 3000;
              setSyncProgress({
                status: 'running',
                message: `Network error, retry ${retryCount}/${MAX_RETRIES} za ${waitTime/1000}s...`
              });
              await new Promise(r => setTimeout(r, waitTime));
              continue;
            }
            throw fetchError;
          }
        }

        if (!response?.ok) {
          throw new Error(data?.error || 'Sync failed after retries');
        }

        // Dry run vraća rezultat odmah
        if (data?.status === 'dry_run') {
          const formattedLogs = (data?.logs || []).map((log: any) => ({
            level: log.level === 'error' ? 'error' : log.level === 'warn' ? 'warning' : 'info',
            message: `[${log.timestamp?.split('T')[1]?.substring(0, 8) || ''}] ${log.message}`,
          }));

          const stats = data?.stats || {};
          setSyncResult({
            results: [{
              created: stats.toCreate || 0,
              updated: stats.toUpdate || 0,
              skipped: stats.toSkip || 0,
              errors: stats.errors || 0,
              deleted: stats.toDelete || 0,
              logs: formattedLogs,
            }],
          });

          setSyncProgress({ status: 'completed', message: 'DRY RUN završen' });
          toast.info(`DRY RUN: ${stats.toCreate || 0} kreirati, ${stats.toUpdate || 0} ažurirati, ${stats.toDelete || 0} obrisati`);
          return; // Exit early for dry run
        }

        // Akumuliraj rezultate iz ovog chunk-a
        const stats = data?.stats || {};
        accumulatedStats.created += stats.created || 0;
        accumulatedStats.updated += stats.updated || 0;
        accumulatedStats.deleted += stats.deleted || 0;
        accumulatedStats.errors += stats.errors || 0;
        // Skipped se računa samo jednom (iz prvog chunk-a)
        if (chunkNumber === 1) {
          accumulatedStats.skipped = stats.skipped || 0;
        }

        // Dodaj logove
        const formattedLogs = (data?.logs || []).map((log: any) => ({
          level: log.level === 'error' ? 'error' : log.level === 'warn' ? 'warning' : 'info',
          message: `[${log.timestamp?.split('T')[1]?.substring(0, 8) || ''}] ${log.message}`,
        }));
        allLogs.push(...formattedLogs);

        // Update za UI
        setSyncResult({
          results: [{
            created: accumulatedStats.created,
            updated: accumulatedStats.updated,
            skipped: accumulatedStats.skipped,
            errors: accumulatedStats.errors,
            deleted: accumulatedStats.deleted,
            logs: allLogs,
          }],
        });

        // Check da li ima još - koristi sessionId za nastavak
        if (data?.status === 'in_progress' && data?.sessionId) {
          hasMore = true;
          currentSessionId = data.sessionId;
          totalTasks = data.totalTasks || 0;
        } else {
          hasMore = false;
        }
      }

      // Sve završeno
      setSyncProgress({ status: 'completed', message: 'FULL SYNC završen!' });
      await loadShops();
      toast.success(`Full Sync: ${accumulatedStats.created} kreirano, ${accumulatedStats.updated} ažurirano, ${accumulatedStats.deleted} za brisanje`);

    } catch (err: any) {
      setSyncProgress({ status: 'error', message: err.message });
      setSyncResult({ error: err.message });
      toast.error(`Full Sync greška: ${err.message}`);
    } finally {
      setSyncing(prev => ({ ...prev, [shop.id!]: false }));
    }
  };

  const toggleShopExpanded = (shopId: string) => {
    setShops(prev => prev.map(shop =>
      shop.id === shopId ? { ...shop, isExpanded: !shop.isExpanded } : shop
    ));
  };

  const toggleShopActive = async (shop: ShopWithLogs) => {
    try {
      await adminApi.shops.update(shop.id, { is_active: !shop.is_active });

      setShops(prev => prev.map(s =>
        s.id === shop.id ? { ...s, is_active: !s.is_active } : s
      ));

      toast.success(`Shop ${!shop.is_active ? 'aktiviran' : 'deaktiviran'}`);
    } catch (err: any) {
      toast.error(`Greška: ${err.message}`);
    }
  };

  // =========================================================================
  // ADD/DELETE SHOP FUNCTIONS
  // =========================================================================

  const testNewShopConnection = async () => {
    if (!newShopData.url || !newShopData.consumer_key || !newShopData.consumer_secret) {
      toast.error('Unesite URL, Consumer Key i Consumer Secret');
      return;
    }

    setTestingConnection(true);
    setConnectionTestResult(null);

    try {
      // Normalize URL
      let url = newShopData.url.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }

      const queryString = new URLSearchParams({
        consumer_key: newShopData.consumer_key.trim(),
        consumer_secret: newShopData.consumer_secret.trim(),
        per_page: '1',
      }).toString();

      const response = await fetch(`${url}/wp-json/wc/v3/products?${queryString}`, {
        method: 'GET',
      });

      if (response.ok) {
        const totalProducts = parseInt(response.headers.get('x-wp-total') || '0');
        setConnectionTestResult({
          success: true,
          message: `Konekcija uspješna! Pronađeno ${totalProducts} proizvoda.`,
        });
      } else if (response.status === 401) {
        setConnectionTestResult({
          success: false,
          message: 'Neispravni API kredencijali (Consumer Key/Secret)',
        });
      } else if (response.status === 404) {
        setConnectionTestResult({
          success: false,
          message: 'WooCommerce API nije pronađen na ovom URL-u',
        });
      } else {
        setConnectionTestResult({
          success: false,
          message: `HTTP greška: ${response.status}`,
        });
      }
    } catch (err: any) {
      setConnectionTestResult({
        success: false,
        message: err.message || 'Greška pri povezivanju',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const addNewShop = async () => {
    if (!newShopData.name || !newShopData.url || !newShopData.consumer_key || !newShopData.consumer_secret) {
      toast.error('Sva polja su obavezna');
      return;
    }

    setAddingShop(true);

    try {
      // Normalize URL
      let url = newShopData.url.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }

      // Default sync fields - sva polja uključena
      const defaultSyncFields = SYNC_FIELD_OPTIONS.reduce((acc, field) => {
        acc[field.key] = true;
        return acc;
      }, {} as Record<string, boolean>);

      await adminApi.shops.create({
        name: newShopData.name.trim(),
        url: url,
        consumer_key: newShopData.consumer_key.trim(),
        consumer_secret: newShopData.consumer_secret.trim(),
        is_active: true,
        sync_fields: defaultSyncFields,
        pricing_config: {},
        source_filters: {},
        plugin_config: newShopData.plugin_enabled && newShopData.plugin_api_key
          ? { enabled: true, api_key: newShopData.plugin_api_key.trim() }
          : null,
      });

      toast.success('Shop uspješno dodan!');
      setAddShopDialogOpen(false);
      setNewShopData({ name: '', url: '', consumer_key: '', consumer_secret: '', plugin_enabled: false, plugin_api_key: '' });
      setConnectionTestResult(null);
      await loadShops();
    } catch (err: any) {
      toast.error(`Greška: ${err.message}`);
    } finally {
      setAddingShop(false);
    }
  };

  const openDeleteDialog = (shop: ShopWithLogs) => {
    setShopToDelete(shop);
    setDeleteShopDialogOpen(true);
  };

  const confirmDeleteShop = async () => {
    if (!shopToDelete) return;

    setDeletingShop(true);

    try {
      await adminApi.shops.remove(shopToDelete.id);

      toast.success(`Shop "${shopToDelete.name}" je obrisan`);
      setDeleteShopDialogOpen(false);
      setShopToDelete(null);
      await loadShops();
    } catch (err: any) {
      toast.error(`Greška: ${err.message}`);
    } finally {
      setDeletingShop(false);
    }
  };

  // =========================================================================
  // HELPERS
  // =========================================================================

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('hr-HR');
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'running': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <SuccessIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'running': return <CircularProgress size={20} />;
      default: return <WarningIcon color="warning" />;
    }
  };

  const getFiltersSummary = (shop: WooShop) => {
    const filters = shop.source_filters || {};
    const parts: string[] = [];

    if (filters.suppliers?.length > 0) {
      parts.push(`Dobavljači: ${filters.suppliers.join(', ')}`);
    }
    if (filters.min_stock_quantity > 0) {
      parts.push(`Min qty: ${filters.min_stock_quantity}`);
    }
    return parts.length > 0 ? parts.join(' | ') : 'Bez filtera';
  };

  const getPricingSummary = (shop: WooShop) => {
    const pricing = shop.pricing_config || {};
    if (!pricing.costPrice?.enabled) return 'Direktno iz source-a';

    const parts: string[] = [];
    if (pricing.regularPriceCalculation?.type === 'markup_percent') {
      parts.push(`Redovna: ${marginDisplay(pricing.regularPriceCalculation.value)}% marže + 17% PDV`);
    }
    if (pricing.salePriceCalculation?.enabled && pricing.salePriceCalculation?.type === 'markup_percent') {
      parts.push(`Akcijska: ${marginDisplay(pricing.salePriceCalculation.value)}% marže + 17% PDV`);
    }
    if (pricing.regularPriceCalculation?.rounding?.type === 'marketing') {
      parts.push('Marketing rounding');
    }

    return parts.length > 0 ? parts.join(' | ') : 'Nije konfigurirano';
  };

  const getSyncFieldsSummary = (shop: WooShop) => {
    const fields = shop.sync_fields || {};
    const enabled = Object.entries(fields).filter(([_, v]) => v).map(([k]) => k);
    if (enabled.length === 0) return 'Sva polja';
    return enabled.slice(0, 5).join(', ') + (enabled.length > 5 ? `... (+${enabled.length - 5})` : '');
  };

  // Ima li shop greške u zadnjem sync-u
  const shopHasErrors = (shop: ShopWithLogs): boolean => {
    if ((shop.last_sync_stats?.errors || 0) > 0) return true;
    const lastLog = shop.recentLogs?.[0];
    return !!lastLog && (lastLog.status === 'failed' || lastLog.status === 'error');
  };

  // Filtrirana lista shopova (search + filter chipovi)
  const filteredShops = shops.filter((shop) => {
    const q = shopSearch.trim().toLowerCase();
    if (q && !`${shop.name || ''} ${shop.url || ''}`.toLowerCase().includes(q)) return false;
    if (shopFilter === 'active') return shop.is_active;
    if (shopFilter === 'inactive') return !shop.is_active;
    if (shopFilter === 'errors') return shopHasErrors(shop);
    return true;
  });

  // Provjeri je li kategorija mapirana (podržava oba formata: sourceValue i sourceFieldValue)
  const isCategoryMapped = (categoryName: string): boolean => {
    const mappings = sourceFilters?.category_custom_field_mappings || [];
    return mappings.some((m: any) => (m.sourceValue || m.sourceFieldValue) === categoryName);
  };

  // Dohvati mapiranje za kategoriju (podržava oba formata: sourceValue i sourceFieldValue)
  const getMappingForCategory = (categoryName: string) => {
    const mappings = sourceFilters?.category_custom_field_mappings || [];
    return mappings.find((m: any) => (m.sourceValue || m.sourceFieldValue) === categoryName);
  };

  // Dodaj novo mapiranje ili ažuriraj postojeće
  const addCategoryMapping = () => {
    if (!newMapping.sourceValue || !newMapping.targetId) {
      toast.error('Molimo unesite source kategoriju i target ID');
      return;
    }

    const currentMappings = sourceFilters?.category_custom_field_mappings || [];

    // Ako editujemo, zamijeni postojeće mapiranje
    if (editingMapping) {
      const updatedMappings = currentMappings.map((m: any) => {
        const src = m.sourceValue || m.sourceFieldValue;
        if (src === editingMapping) {
          return {
            sourceValue: newMapping.sourceValue,
            targetId: parseInt(newMapping.targetId),
            targetName: newMapping.targetName || '',
          };
        }
        return m;
      });

      setSourceFilters((prev: any) => ({
        ...prev,
        category_custom_field_mappings: updatedMappings,
      }));

      setEditingMapping(null);
      setNewMapping({ sourceValue: '', targetId: '', targetName: '' });
      toast.success(`Ažurirano mapiranje: ${newMapping.sourceValue}`);
      return;
    }

    // Provjeri da li već postoji (samo za novo mapiranje)
    if (isCategoryMapped(newMapping.sourceValue)) {
      toast.error('Ova kategorija je već mapirana. Klikni na nju za editovanje.');
      return;
    }

    const updatedMappings = [
      ...currentMappings,
      {
        sourceValue: newMapping.sourceValue,
        targetId: parseInt(newMapping.targetId),
        targetName: newMapping.targetName || '',
      },
    ];

    setSourceFilters((prev: any) => ({
      ...prev,
      category_custom_field_mappings: updatedMappings,
    }));

    // Reset form
    setNewMapping({ sourceValue: '', targetId: '', targetName: '' });
    toast.success(`Dodano mapiranje: ${newMapping.sourceValue}`);
  };

  // Započni editovanje postojećeg mapiranja
  const startEditMapping = (sourceValue: string) => {
    const mapping = getMappingForCategory(sourceValue);
    if (mapping) {
      setEditingMapping(sourceValue);
      setNewMapping({
        sourceValue: sourceValue,
        targetId: String(mapping.targetId || ''),
        targetName: mapping.targetName || '',
      });
    }
  };

  // Otkaži editovanje
  const cancelEditMapping = () => {
    setEditingMapping(null);
    setNewMapping({ sourceValue: '', targetId: '', targetName: '' });
  };

  // Obriši mapiranje (podržava oba formata: sourceValue i sourceFieldValue)
  const removeCategoryMapping = (sourceValue: string) => {
    const updatedMappings = (sourceFilters?.category_custom_field_mappings || [])
      .filter((m: any) => (m.sourceValue || m.sourceFieldValue) !== sourceValue);

    setSourceFilters((prev: any) => ({
      ...prev,
      category_custom_field_mappings: updatedMappings,
    }));

    toast.success(`Uklonjeno mapiranje za: ${sourceValue}`);
  };

  // =========================================================================
  // ATTRIBUTE MAPPING FUNCTIONS
  // =========================================================================

  // Primijeni JSON za attribute mapping
  const applyAttributeMappingJson = () => {
    try {
      const parsed = JSON.parse(attributeMappingJson);

      if (!Array.isArray(parsed)) {
        throw new Error('JSON mora biti array');
      }

      const mappings = parsed.map((item: any, index: number) => {
        if (!item.sourceName || !item.targetName) {
          throw new Error(`Stavka ${index + 1}: sourceName i targetName su obavezni`);
        }
        return {
          sourceName: String(item.sourceName),
          targetName: String(item.targetName),
          enabled: item.enabled !== false,
        };
      });

      setSourceFilters((prev: any) => ({
        ...prev,
        attribute_mappings: {
          enabled: true,
          mappings,
          unmappedBehavior: prev?.attribute_mappings?.unmappedBehavior || 'keep_original',
        },
      }));

      setAttributeMappingError(null);
      setAttributeMappingJson('');
      toast.success(`Dodano ${mappings.length} mapiranja atributa`);
    } catch (e: any) {
      setAttributeMappingError(e.message || 'Neispravan JSON format');
    }
  };

  // Obriši jedno mapiranje atributa
  const removeAttributeMapping = (sourceName: string) => {
    const currentMappings = sourceFilters?.attribute_mappings?.mappings || [];
    const updatedMappings = currentMappings.filter((m: any) => m.sourceName !== sourceName);

    setSourceFilters((prev: any) => ({
      ...prev,
      attribute_mappings: {
        ...prev?.attribute_mappings,
        mappings: updatedMappings,
      },
    }));

    toast.success(`Uklonjeno mapiranje za: ${sourceName}`);
  };

  // Obriši sva mapiranja atributa
  const clearAllAttributeMappings = () => {
    setSourceFilters((prev: any) => ({
      ...prev,
      attribute_mappings: {
        ...prev?.attribute_mappings,
        mappings: [],
      },
    }));
    toast.success('Sva mapiranja atributa su obrisana');
  };

  // Učitaj atribute iz Supabase specifikacije kolone
  const loadSupabaseAttributes = async () => {
    setLoadingAttributes(true);
    try {
      // Direct aggregation through the admin API (RPC path removed).
      const products = (await adminApi.products.list({
        fields: ['specifikacije'],
        has_specifikacije: true,
        limit: 500,
      })) as any[];

      const keysSet = new Set<string>();
      products?.forEach((p: any) => {
        if (p.specifikacije && typeof p.specifikacije === 'object') {
          Object.keys(p.specifikacije).forEach((key) => keysSet.add(key));
        }
      });

      const sortedKeys = Array.from(keysSet).sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );
      setSupabaseAttributes(sortedKeys);
    } catch (err: any) {
      console.error('Greška pri učitavanju atributa:', err);
      toast.error('Greška pri učitavanju atributa iz Supabase');
    } finally {
      setLoadingAttributes(false);
    }
  };

  // Provjeri da li je atribut već mapiran
  const isAttributeMapped = (attrName: string) => {
    const mappings = sourceFilters?.attribute_mappings?.mappings || [];
    return mappings.some((m: any) => m.sourceName === attrName);
  };

  // Dodaj pojedinačno mapiranje atributa
  const addSingleAttributeMapping = () => {
    if (!selectedAttribute || !targetAttributeName.trim()) {
      toast.error('Odaberi source atribut i unesi target naziv');
      return;
    }

    if (isAttributeMapped(selectedAttribute)) {
      toast.error('Ovaj atribut je već mapiran');
      return;
    }

    const currentMappings = sourceFilters?.attribute_mappings?.mappings || [];
    const newMapping = {
      sourceName: selectedAttribute,
      targetName: targetAttributeName.trim(),
      enabled: true,
    };

    setSourceFilters((prev: any) => ({
      ...prev,
      attribute_mappings: {
        ...prev?.attribute_mappings,
        enabled: true,
        mappings: [...currentMappings, newMapping],
        unmappedBehavior: prev?.attribute_mappings?.unmappedBehavior || 'keep_original',
      },
    }));

    setSelectedAttribute(null);
    setTargetAttributeName('');
    toast.success(`Dodano mapiranje: ${selectedAttribute} → ${targetAttributeName}`);
  };

  // Filtrirani atributi
  const getFilteredAttributes = () => {
    let attrs = supabaseAttributes;

    if (attributeSearch) {
      attrs = attrs.filter(attr =>
        attr.toLowerCase().includes(attributeSearch.toLowerCase())
      );
    }

    return attrs;
  };

  // Filtrirane kategorije
  const getFilteredCategories = () => {
    let categories = supabaseCategories;

    // Filter po pretrazi
    if (categorySearch) {
      categories = categories.filter(cat =>
        cat.toLowerCase().includes(categorySearch.toLowerCase())
      );
    }

    // Filter samo nemapirane
    if (showOnlyUnmapped) {
      categories = categories.filter(cat => !isCategoryMapped(cat));
    }

    return categories;
  };

  // =========================================================================
  // LIVE PREVIEW FUNCTIONS
  // =========================================================================

  const loadRandomProductForPreview = async (shop: ShopWithLogs) => {
    setLoadingPreview(true);
    setPreviewShop(shop);
    setPreviewDialogOpen(true);
    setPreviewProduct(null);
    setPreviewMappedProduct(null);
    setPreviewTargetProduct(null);

    try {
      const filters = shop.source_filters || {};
      const baseFilter = {
        dobavljac: filters.suppliers?.length > 0 ? filters.suppliers : undefined,
        min_stock_quantity: filters.min_stock_quantity > 0 ? filters.min_stock_quantity : undefined,
      };

      // Let the server pick a random row by fetching a shuffled sample.
      const products = (await adminApi.products.list({
        ...baseFilter,
        limit: 1,
        random: true,
      })) as any[];

      if (!products || products.length === 0) {
        toast.error('Nema proizvoda koji odgovaraju filterima');
        setLoadingPreview(false);
        return;
      }

      const product = products[0];
      setPreviewProduct(product);

      // Dohvati proizvod iz Target WooCommerce po SKU
      let targetProduct = null;
      const sku = product.sku || product.sifra;
      if (sku && shop.url && shop.consumer_key && shop.consumer_secret) {
        try {
          // Koristi proxy za dohvat (izbjegava CORS)
          const proxyUrl = `/api/woo-proxy?url=${encodeURIComponent(shop.url)}&endpoint=products&sku=${encodeURIComponent(sku)}&consumer_key=${encodeURIComponent(shop.consumer_key)}&consumer_secret=${encodeURIComponent(shop.consumer_secret)}&per_page=1`;

          console.log(`[Preview] Tražim SKU ${sku} u target shopu...`);
          const targetResponse = await fetch(proxyUrl);

          if (targetResponse.ok) {
            const targetData = await targetResponse.json();
            console.log(`[Preview] Target search for SKU ${sku}:`, targetData);
            if (targetData && Array.isArray(targetData) && targetData.length > 0) {
              targetProduct = targetData[0];
              console.log(`[Preview] Pronađen proizvod ID: ${targetProduct.id}`);
            } else {
              console.log(`[Preview] SKU ${sku} nije pronađen u target shopu - CREATE`);
            }
          } else {
            const errorData = await targetResponse.json().catch(() => ({}));
            console.log(`[Preview] Target search failed:`, targetResponse.status, errorData);
          }
        } catch (e) {
          console.log('[Preview] Target product fetch failed:', e);
        }
      }
      setPreviewTargetProduct(targetProduct);

      // DEBUG: Provjeri custom_field_mappings
      console.log('[Preview] Shop custom_field_mappings:', (shop as any).custom_field_mappings);

      // Koristi CENTRALNU LOGIKU za mapiranje
      const syncConfig: SyncConfig = {
        shop: shop as any, // WooShop
        sourceProduct: product,
        targetProduct: targetProduct,
      };

      const { data, action, priceCalculation } = buildProductData(syncConfig);

      // Dodaj extra info za prikaz
      const mapped = {
        ...data,
        _isExisting: action === 'update',
        _targetProductId: targetProduct?.id,
        _price_calculation: priceCalculation,
        _statusNote: action === 'update' ? 'Zadržava se postojeći status' : 'Novi proizvod',
        // Za prikaz kategorija sa mapping info
        categories: mapCategoriesForDisplay(product, shop),
        // Za prikaz atributa
        attributes: mapAttributesForDisplay(product, shop),
      };

      setPreviewMappedProduct(mapped);

    } catch (err: any) {
      console.error('Error loading preview product:', err);
      toast.error(`Greška: ${err.message}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Učitaj proizvod po SKU za preview
  const loadProductBySkuForPreview = async (shop: ShopWithLogs, sku: string) => {
    if (!sku.trim()) {
      toast.error('Unesi SKU');
      return;
    }

    setLoadingPreview(true);
    setPreviewShop(shop);
    setPreviewProduct(null);
    setPreviewMappedProduct(null);
    setPreviewTargetProduct(null);

    try {
      // Dohvati proizvod po SKU (case-insensitive)
      let product: any = null;
      try {
        product = await adminApi.products.getBySku(sku.trim());
      } catch {
        product = null;
      }

      if (!product) {
        toast.error(`Proizvod sa SKU "${sku}" nije pronađen`);
        setLoadingPreview(false);
        return;
      }

      setPreviewProduct(product);

      // Provjeri da li postoji na WooCommerce
      const targetProduct = await findTargetProduct(shop as any, product.sku);
      setPreviewTargetProduct(targetProduct);

      // Koristi CENTRALNU LOGIKU za mapiranje
      const syncConfig: SyncConfig = {
        shop: shop as any,
        sourceProduct: product,
        targetProduct: targetProduct,
      };

      const { data, action, priceCalculation } = buildProductData(syncConfig);

      const mapped = {
        ...data,
        _isExisting: action === 'update',
        _targetProductId: targetProduct?.id,
        _price_calculation: priceCalculation,
        _statusNote: action === 'update' ? 'Zadržava se postojeći status' : 'Novi proizvod',
        categories: mapCategoriesForDisplay(product, shop),
        attributes: mapAttributesForDisplay(product, shop),
      };

      setPreviewMappedProduct(mapped);

    } catch (err: any) {
      console.error('Error loading preview by SKU:', err);
      toast.error(`Greška: ${err.message}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Helper za prikaz kategorija sa mapping statusom
  const mapCategoriesForDisplay = (product: any, shop: ShopWithLogs) => {
    const sourceFilters = shop.source_filters || {};
    const categoryMappings = sourceFilters.category_custom_field_mappings || [];
    let sourceCategories = product.categories || '';

    if (typeof sourceCategories === 'string' && sourceCategories) {
      sourceCategories = sourceCategories.replace(/[()]/g, '').split(/[|>]/).map((s: string) => s.trim()).filter(Boolean);
    } else if (!Array.isArray(sourceCategories)) {
      sourceCategories = [];
    }

    return sourceCategories.map((cat: string) => {
      const mapping = categoryMappings.find((m: any) =>
        (m.sourceValue || m.sourceFieldValue) === cat ||
        (m.sourceValue || m.sourceFieldValue)?.toLowerCase() === cat.toLowerCase()
      );
      if (mapping?.targetId) {
        return { id: mapping.targetId, name: mapping.targetName || cat, _mapped: true };
      }
      return { name: cat, _mapped: false };
    });
  };

  // Helper za prikaz atributa
  const mapAttributesForDisplay = (product: any, shop: ShopWithLogs) => {
    const sourceFilters = shop.source_filters || {};
    const attributeMappings = sourceFilters.attribute_mappings || [];
    const specifikacije = product.specifikacije || {};

    const attrs: any[] = [];

    // Brand
    if (product.brand) {
      attrs.push({ name: 'pa_brend', options: [product.brand], _mapped: true });
    }

    // Garancija (formatirano kao "X Mjeseci")
    if (product.garancija) {
      const garancija = String(product.garancija).trim();
      let mjeseci: number | null = null;

      if (/^\d+$/.test(garancija)) {
        mjeseci = parseInt(garancija, 10);
      } else if (/^\d+M$/i.test(garancija)) {
        mjeseci = parseInt(garancija, 10);
      } else if (/^\d+Y$/i.test(garancija)) {
        mjeseci = parseInt(garancija, 10) * 12;
      }

      if (mjeseci && mjeseci > 0) {
        attrs.push({ name: 'pa_garancija', options: [`${mjeseci} Mjeseci`], _mapped: true });
      }
    }

    // Specifikacije
    for (const [key, value] of Object.entries(specifikacije)) {
      if (!value) continue;
      const mapping = attributeMappings.find((m: any) => m.sourceAttribute === key);
      attrs.push({
        name: mapping?.targetAttribute || key,
        options: [String(value)],
        _source: key,
        _mapped: !!mapping?.targetAttribute,
      });
    }

    return attrs;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SYNC POJEDINAČNOG PROIZVODA (koristi centralnu logiku)
  // ═══════════════════════════════════════════════════════════════════════════
  const handleSyncPreviewProduct = async () => {
    if (!previewShop || !previewProduct) return;

    setSyncingPreview(true);
    try {
      const syncConfig: SyncConfig = {
        shop: previewShop as any,
        sourceProduct: previewProduct,
        targetProduct: previewTargetProduct,
      };

      const result = await syncSingleProduct(syncConfig);

      if (result.success) {
        toast.success(`${result.action === 'create' ? 'Kreiran' : 'Ažuriran'} proizvod: ${result.sku} (ID: ${result.productId})`);

        // Reload target product to show updated state
        const updatedTarget = await findTargetProduct(previewShop as any, previewProduct.sku);
        setPreviewTargetProduct(updatedTarget);

        // Rebuild preview
        const newConfig: SyncConfig = {
          shop: previewShop as any,
          sourceProduct: previewProduct,
          targetProduct: updatedTarget,
        };
        const { data, action, priceCalculation } = buildProductData(newConfig);
        setPreviewMappedProduct({
          ...data,
          _isExisting: action === 'update',
          _targetProductId: updatedTarget?.id,
          _price_calculation: priceCalculation,
          _statusNote: action === 'update' ? 'Zadržava se postojeći status' : 'Novi proizvod',
          categories: mapCategoriesForDisplay(previewProduct, previewShop),
          attributes: mapAttributesForDisplay(previewProduct, previewShop),
        });
      } else {
        toast.error(`Greška: ${result.error}`);
      }
    } catch (err: any) {
      toast.error(`Greška: ${err.message}`);
    } finally {
      setSyncingPreview(false);
    }
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudSyncIcon sx={{ fontSize: 40 }} color="primary" />
            WooSync
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Supabase → WooCommerce sinhronizacija
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={() => setAddShopDialogOpen(true)}
          >
            Dodaj Shop
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadShops}
          >
            Osvježi
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search + Filter Toolbar */}
      {shops.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Pretraži shopove po imenu ili URL-u..."
            value={shopSearch}
            onChange={(e) => setShopSearch(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {([
              { key: 'all' as const, label: `Svi (${shops.length})` },
              { key: 'active' as const, label: `Aktivni (${shops.filter(s => s.is_active).length})` },
              { key: 'inactive' as const, label: `Neaktivni (${shops.filter(s => !s.is_active).length})` },
              { key: 'errors' as const, label: `Greške (${shops.filter(shopHasErrors).length})` },
            ]).map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                color={f.key === 'errors' ? 'error' : 'primary'}
                variant={shopFilter === f.key ? 'filled' : 'outlined'}
                onClick={() => setShopFilter(f.key)}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Shops Table */}
      {shops.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <StoreIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nema konfiguriranih shopova
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Dodajte shopove u <code>woo_shops</code> tabelu
          </Typography>
        </Card>
      ) : filteredShops.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nema shopova za zadane kriterije
          </Typography>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell sx={{ fontWeight: 700 }}>Shop</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Zadnji sync</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rezultat</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Akcije</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredShops.map((shop) => (
                <Fragment key={shop.id}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      opacity: shop.is_active ? 1 : 0.6,
                      '& > *': { borderBottom: shop.isExpanded ? 'unset' : undefined },
                    }}
                    onClick={() => toggleShopExpanded(shop.id!)}
                  >
                    {/* Status dot + expand caret */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={shop.is_active ? 'Aktivan' : 'Neaktivan'}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: shop.is_active ? 'success.main' : 'grey.400', flexShrink: 0 }} />
                        </Tooltip>
                        {shop.isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                      </Box>
                    </TableCell>

                    {/* Shop name + URL */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{shop.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{shop.url}</Typography>
                    </TableCell>

                    {/* Last sync / live progress */}
                    <TableCell>
                      {activeSessions[shop.id!] ? (
                        <Chip
                          icon={<CircularProgress size={12} />}
                          label={`${activeSessions[shop.id!]!.progress}%`}
                          color="info"
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                          onDelete={(e) => { e.stopPropagation(); cancelActiveSession(shop.id!); }}
                          deleteIcon={cancelingSession[shop.id!] ? <CircularProgress size={12} /> : <CancelIcon />}
                          sx={{ height: 24, '& .MuiChip-label': { px: 0.5 } }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {shop.last_sync_at ? formatDate(shop.last_sync_at) : 'Nikad'}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Result chips */}
                    <TableCell>
                      {shop.last_sync_stats ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip size="small" label={`+${shop.last_sync_stats.created || 0}`} color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                          <Chip size="small" label={`~${shop.last_sync_stats.updated || 0}`} color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                          {(shop.last_sync_stats.errors || 0) > 0 && (
                            <Chip size="small" label={`!${shop.last_sync_stats.errors}`} color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<SettingsIcon />}
                          onClick={() => openSettings(shop)}
                          sx={{ fontSize: '0.75rem', py: 0.5 }}
                        >
                          Postavke
                        </Button>
                        <IconButton
                          size="small"
                          onClick={(e) => setActionMenuAnchor({ el: e.currentTarget, shopId: shop.id! })}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded detail row */}
                  <TableRow>
                    <TableCell sx={{ py: 0, borderBottom: shop.isExpanded ? undefined : 'none' }} colSpan={5}>
                      <Collapse in={shop.isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1.5, px: 1 }}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              <strong>Filteri:</strong> {getFiltersSummary(shop)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              <strong>Pricing:</strong> {getPricingSummary(shop)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              <strong>Sync polja:</strong> {getSyncFieldsSummary(shop)}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <HistoryIcon sx={{ fontSize: 14 }} /> Zadnji sync-ovi
                          </Typography>
                          {shop.recentLogs && shop.recentLogs.length > 0 ? (
                            <Box sx={{ fontSize: '0.75rem' }}>
                              {shop.recentLogs.slice(0, 3).map((log) => (
                                <Box key={log.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.25 }}>
                                  <Chip
                                    size="small"
                                    label={log.status}
                                    color={getStatusColor(log.status)}
                                    sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(log.started_at)}
                                  </Typography>
                                  <Typography variant="caption">
                                    +{log.products_created || 0} ~{log.products_updated || 0}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Nema prethodnih sync operacija
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Action Menu for Shops */}
      <Menu
        anchorEl={actionMenuAnchor?.el}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {(() => {
          const menuShop = shops.find(s => s.id === actionMenuAnchor?.shopId);
          return (
            <MenuItem
              onClick={() => {
                if (menuShop) toggleShopActive(menuShop);
                setActionMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                {menuShop?.is_active
                  ? <CancelIcon fontSize="small" />
                  : <SuccessIcon fontSize="small" color="success" />}
              </ListItemIcon>
              <ListItemText>{menuShop?.is_active ? 'Deaktiviraj' : 'Aktiviraj'}</ListItemText>
            </MenuItem>
          );
        })()}
        <Divider />
        <MenuItem
          onClick={() => {
            const shop = shops.find(s => s.id === actionMenuAnchor?.shopId);
            if (shop) openSettings(shop);
            setActionMenuAnchor(null);
          }}
        >
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Postavke</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const shop = shops.find(s => s.id === actionMenuAnchor?.shopId);
            if (shop) loadRandomProductForPreview(shop);
            setActionMenuAnchor(null);
          }}
        >
          <ListItemIcon><PreviewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Preview mapiranja</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const shop = shops.find(s => s.id === actionMenuAnchor?.shopId);
            if (shop) triggerFullSync(shop, { dryRun: false, deleteAction: 'draft' });
            setActionMenuAnchor(null);
          }}
        >
          <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Full Sync</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const shop = shops.find(s => s.id === actionMenuAnchor?.shopId);
            if (shop) triggerFullSync(shop, { dryRun: true });
            setActionMenuAnchor(null);
          }}
        >
          <ListItemIcon><SearchIcon fontSize="small" color="warning" /></ListItemIcon>
          <ListItemText>Dry Run</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const shop = shops.find(s => s.id === actionMenuAnchor?.shopId);
            if (shop) checkAndShowSession(shop.id!);
            setActionMenuAnchor(null);
          }}
        >
          <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Provjeri sesiju</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const shop = shops.find(s => s.id === actionMenuAnchor?.shopId);
            if (shop) openDeleteDialog(shop);
            setActionMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Obriši shop</ListItemText>
        </MenuItem>
      </Menu>

      {/* =====================================================================
          SETTINGS DIALOG
          ===================================================================== */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          Postavke: {editingShop?.name}
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={settingsTab}
            onChange={(_, v) => setSettingsTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<FieldsIcon />} label="Sync Polja" />
            <Tab icon={<PriceIcon />} label="Pricing" />
            <Tab icon={<FilterIcon />} label="Filteri" />
            <Tab icon={<CategoryIcon />} label="Kategorije" />
            <Tab icon={<SettingsIcon />} label="Atributi" />
            <Tab icon={<TextIcon />} label="Nazivi" />
            <Tab icon={<DatabaseIcon />} label="Custom Fields" />
            <Tab icon={<TriggerIcon />} label="Webhook Trigger" />
            <Tab icon={<PluginIcon />} label="SejkanSync Plugin" />
            <Tab icon={<PremiumIcon />} label="Premium" sx={{ '& .MuiTab-iconWrapper': { color: '#f59e0b' } }} />
            <Tab icon={<QueueIcon />} label="Menu Order" />
          </Tabs>

          {/* TAB 0: Sync Fields */}
          {settingsTab === 0 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Odaberite koja polja će se sinkronizirati iz Supabase u WooCommerce.
                Ako ništa nije odabrano, sinkroniziraju se sva polja.
              </Alert>

              {/* Način synca - GLAVNA OPCIJA */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '2px solid', borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                  🔄 Način sinkronizacije
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Koje operacije će sync izvršavati?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={(syncOptions.sync_mode || 'create_and_update') === 'create_and_update'}
                        onChange={() => setSyncOptions(prev => ({ ...prev, sync_mode: 'create_and_update' }))}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Kreiraj i ažuriraj</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Kreira nove proizvode, ažurira postojeće i briše nedostajuće
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={syncOptions.sync_mode === 'create_and_update_managed'}
                        onChange={() => setSyncOptions(prev => ({ ...prev, sync_mode: 'create_and_update_managed' }))}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Kreiraj + ažuriraj + briši samo naše</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Kreira nove proizvode u DRAFT statusu. Ažurira postojeće sa istim SKU. Briše SAMO one proizvode koje smo MI ranije sinkronizirali iz Supabase (markirane sa _sejkan_sync_origin), a kojih više nema. Sve ostale Woo proizvode ne dira.
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={syncOptions.sync_mode === 'create_only'}
                        onChange={() => setSyncOptions(prev => ({ ...prev, sync_mode: 'create_only' }))}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Samo kreiranje</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Samo kreira nove proizvode i briše nedostajuće, NE ažurira postojeće
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={syncOptions.sync_mode === 'update_only'}
                        onChange={() => setSyncOptions(prev => ({ ...prev, sync_mode: 'update_only' }))}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Samo ažuriranje</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Samo ažurira postojeće proizvode, NE kreira nove niti briše
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={syncOptions.sync_mode === 'delete_only'}
                        onChange={() => setSyncOptions(prev => ({ ...prev, sync_mode: 'delete_only' }))}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Samo brisanje</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Samo briše proizvode koji nisu u source-u, NE kreira niti ažurira
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>

              {/* Brisanje nedostajućih proizvoda */}
              <Box sx={{ mb: 3, p: 2, bgcolor: syncOptions.delete_products !== false ? 'warning.50' : 'background.paper', borderRadius: 1, border: '1px solid', borderColor: syncOptions.delete_products !== false ? 'warning.main' : 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  🗑️ Brisanje nedostajućih proizvoda
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncOptions.delete_products !== false}
                      onChange={(e) => setSyncOptions(prev => ({ ...prev, delete_products: e.target.checked }))}
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {syncOptions.delete_products !== false ? 'Uključeno - briše nedostajuće' : 'Isključeno - NE briše ništa'}
                      </Typography>
                    </Box>
                  }
                />
                {syncOptions.delete_products !== false ? (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Šta se dešava kad proizvod nije u source-u:
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
                      <li>Proizvod ide u <strong>DRAFT</strong> status (nije vidljiv kupcima)</li>
                      <li>Dodaje se <code>_brisanje: DA</code> meta polje</li>
                      <li>Zalihe se postavljaju na 0</li>
                      <li>Pamti se prethodni status za kasniji restore</li>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      ✨ Ako se proizvod vrati u source, automatski se RESTORE-a na prethodni status!
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    <Typography variant="body2">
                      Proizvodi koji nisu u source-u neće biti dirani. Samo CREATE i UPDATE operacije će se izvršavati.
                    </Typography>
                  </Alert>
                )}
              </Box>

              {/* Brisanje po dobavljaču */}
              {syncOptions.delete_products !== false && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    Akcija brisanja po dobavljaču
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Za svakog dobavljača možete odrediti šta se dešava kad proizvod nestane iz source-a.
                    Default je <strong>Draft</strong> (soft-delete). <strong>Trajno brisanje</strong> potpuno briše proizvod iz WooCommerce-a.
                  </Typography>

                  {(syncOptions.supplier_delete_actions || []).length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                      {(syncOptions.supplier_delete_actions || []).map((rule, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: rule.delete_action === 'permanent_delete' ? '#fef2f2' : '#f0fdf4', borderRadius: 1, border: '1px solid', borderColor: rule.delete_action === 'permanent_delete' ? '#fca5a5' : '#bbf7d0' }}>
                          <Autocomplete
                            size="small"
                            options={availableSuppliers.filter(s => !syncOptions.supplier_delete_actions?.some((r, i) => i !== idx && r.supplier === s))}
                            value={rule.supplier || null}
                            onChange={(_, value) => {
                              setSyncOptions(prev => {
                                const updated = [...(prev.supplier_delete_actions || [])];
                                updated[idx] = { ...updated[idx], supplier: value || '' };
                                return { ...prev, supplier_delete_actions: updated };
                              });
                            }}
                            freeSolo
                            renderInput={(params) => <TextField {...params} placeholder="Dobavljač" />}
                            sx={{ minWidth: 200 }}
                          />
                          <TextField
                            select
                            size="small"
                            value={rule.delete_action}
                            onChange={(e) => {
                              setSyncOptions(prev => {
                                const updated = [...(prev.supplier_delete_actions || [])];
                                updated[idx] = { ...updated[idx], delete_action: e.target.value as 'draft' | 'permanent_delete' };
                                return { ...prev, supplier_delete_actions: updated };
                              });
                            }}
                            sx={{ minWidth: 200 }}
                          >
                            <MenuItem value="draft">Draft (soft-delete)</MenuItem>
                            <MenuItem value="permanent_delete">Trajno brisanje</MenuItem>
                          </TextField>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSyncOptions(prev => {
                                const updated = [...(prev.supplier_delete_actions || [])];
                                updated.splice(idx, 1);
                                return { ...prev, supplier_delete_actions: updated };
                              });
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setSyncOptions(prev => ({
                        ...prev,
                        supplier_delete_actions: [
                          ...(prev.supplier_delete_actions || []),
                          { supplier: '', delete_action: 'draft' },
                        ],
                      }));
                    }}
                  >
                    Dodaj dobavljača
                  </Button>

                  {(syncOptions.supplier_delete_actions || []).some(r => r.delete_action === 'permanent_delete') && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Upozorenje:</strong> Proizvodi dobavljača sa "Trajno brisanje" će biti POTPUNO obrisani iz WooCommerce-a (nema restore mogućnosti).
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              {/* Status novih proizvoda */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Status novih proizvoda
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Kada se kreira novi proizvod u WooCommerce, koji status će imati?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={(syncOptions.new_product_status || 'draft') === 'draft'}
                        onChange={() => setSyncOptions(prev => ({ ...prev, new_product_status: 'draft' }))}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Draft (Skica)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Proizvodi neće biti vidljivi kupcima dok ih ne objavite
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={syncOptions.new_product_status === 'publish'}
                        onChange={() => setSyncOptions(prev => ({ ...prev, new_product_status: 'publish' }))}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Publish (Objavljeno)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Proizvodi će odmah biti vidljivi kupcima
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>

              {/* Način ažuriranja atributa */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Način ažuriranja atributa
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Kako se atributi ažuriraju na postojećim proizvodima?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={(syncOptions.attributes_mode || 'smart') === 'smart'}
                        onChange={() => setSyncOptions(prev => ({ ...prev, attributes_mode: 'smart' }))}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Smart (Pametno)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Dodaje nove atribute, ne dira postojeće
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={syncOptions.attributes_mode === 'replace_all'}
                        onChange={() => setSyncOptions(prev => ({ ...prev, attributes_mode: 'replace_all' }))}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Zamijeni sve</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Potpuno zamijeni sve atribute novima iz source-a
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>

              {/* Glavni atributi po shopu — pošalji / ne šalji / mapiraj naziv */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Glavni atributi za ovaj shop
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Opciono. Za svaki atribut: <strong>Pošalji</strong> (default),{' '}
                  <strong>Ne šalji</strong> (skip), ili <strong>Mapiraj</strong> —
                  upiši slug atributa koji ovaj shop koristi (npr. <code>pa_processor</code>).
                </Typography>
                {mainAttributes.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Lista atributa se učitava…
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {mainAttributes.map(attr => {
                      const excluded = syncOptions.excluded_attributes || [];
                      const nameMap = syncOptions.attribute_name_map || {};
                      const isSkipped = excluded.includes(attr.slug);
                      const mappedSlug = nameMap[attr.slug];
                      const mode = isSkipped
                        ? '__skip__'
                        : (mappedSlug !== undefined ? '__map__' : '');
                      return (
                        <Box key={attr.slug} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography variant="body2" sx={{ width: 200, flexShrink: 0 }}>
                            {attr.label}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select
                              value={mode}
                              displayEmpty
                              onChange={(e) => {
                                const newMode = e.target.value as string;
                                setSyncOptions(prev => {
                                  const nextExcluded = (prev.excluded_attributes || [])
                                    .filter(s => s !== attr.slug);
                                  const nextMap = { ...(prev.attribute_name_map || {}) };
                                  delete nextMap[attr.slug];
                                  if (newMode === '__skip__') {
                                    nextExcluded.push(attr.slug);
                                  } else if (newMode === '__map__') {
                                    // upiši prazan string dok korisnik ne ukuca slug
                                    nextMap[attr.slug] = '';
                                  }
                                  return {
                                    ...prev,
                                    excluded_attributes: nextExcluded,
                                    attribute_name_map: nextMap,
                                  };
                                });
                              }}
                            >
                              <MenuItem value="">Pošalji (default)</MenuItem>
                              <MenuItem value="__skip__">Ne šalji (skip)</MenuItem>
                              <MenuItem value="__map__">Mapiraj naziv…</MenuItem>
                            </Select>
                          </FormControl>
                          {mode === '__map__' && (
                            <TextField
                              size="small"
                              placeholder="npr. pa_processor"
                              value={mappedSlug || ''}
                              onChange={(e) => {
                                const slug = e.target.value;
                                setSyncOptions(prev => ({
                                  ...prev,
                                  attribute_name_map: {
                                    ...(prev.attribute_name_map || {}),
                                    [attr.slug]: slug,
                                  },
                                }));
                              }}
                              sx={{ minWidth: 220 }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {['Osnovni podaci', 'Cijene', 'Zalihe', 'Opisi', 'Taksonomije', 'Mediji', 'Dimenzije'].map(category => {
                const fieldsInCategory = SYNC_FIELD_OPTIONS.filter(f => f.category === category);
                if (fieldsInCategory.length === 0) return null;

                return (
                  <Box key={category} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {category}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {fieldsInCategory.map(field => (
                        <FormControlLabel
                          key={field.key}
                          control={
                            <Checkbox
                              checked={syncFields[field.key] ?? true}
                              onChange={(e) => setSyncFields(prev => ({
                                ...prev,
                                [field.key]: e.target.checked
                              }))}
                              size="small"
                            />
                          }
                          label={field.label}
                          sx={{ minWidth: 200 }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}

              {/* Izuzeci po kategorijama (source_filters.category_field_exclusions).
                  Stoji uz sync polja jer je to isto podešavanje, samo suženo na kategoriju. */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon fontSize="small" color="primary" />
                Izuzeci po kategorijama
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Polja označena gore sinhroniziraju se za sve proizvode — <strong>osim</strong> za kategorije
                navedene ovdje. Primjer: da cijene ostanu ručno postavljene za jednu kategoriju, odaberi tu
                kategoriju i polje "Redovna cijena". Važi samo za postojeće proizvode; novi se kreiraju kompletni.
              </Alert>

              {(sourceFilters?.category_field_exclusions || []).map((rule: any, idx: number) => {
                const updateRule = (patch: any) => setSourceFilters((prev: any) => {
                  const rules = [...(prev.category_field_exclusions || [])];
                  rules[idx] = { ...rules[idx], ...patch };
                  return { ...prev, category_field_exclusions: rules };
                });
                return (
                  <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2, opacity: rule.enabled === false ? 0.6 : 1 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={5}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Kategorije (kako ih šalje dobavljač)
                        </Typography>
                        <SupplierCategoriesFilter
                          shopId={editingShop?.id}
                          value={rule.categories || []}
                          onChange={(cats) => updateRule({ categories: cats })}
                          placeholder="Odaberi kategorije..."
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Polja koja se NE ažuriraju
                        </Typography>
                        <Autocomplete
                          multiple
                          size="small"
                          options={CATEGORY_EXCLUDABLE_FIELDS.map(f => f.key)}
                          getOptionLabel={(key: string) => CATEGORY_EXCLUDABLE_FIELDS.find(f => f.key === key)?.label || key}
                          value={rule.fields || []}
                          onChange={(_, fields) => updateRule({ fields })}
                          renderTags={(tagValue, getTagProps) =>
                            tagValue.map((key: string, index: number) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={key}
                                label={CATEGORY_EXCLUDABLE_FIELDS.find(f => f.key === key)?.label || key}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField {...params} placeholder={(rule.fields || []).length === 0 ? 'Odaberi polja...' : ''} />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6} md={1} sx={{ textAlign: 'center' }}>
                        <Switch
                          checked={rule.enabled !== false}
                          onChange={(e) => updateRule({ enabled: e.target.checked })}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6} md={1} sx={{ textAlign: 'center' }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setSourceFilters((prev: any) => ({
                            ...prev,
                            category_field_exclusions: (prev.category_field_exclusions || []).filter((_: any, i: number) => i !== idx),
                          }))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}

              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setSourceFilters((prev: any) => ({
                  ...prev,
                  category_field_exclusions: [
                    ...(prev.category_field_exclusions || []),
                    { enabled: true, categories: [], fields: [] },
                  ],
                }))}
              >
                Dodaj izuzetak
              </Button>
            </Box>
          )}

          {/* TAB 1: Pricing */}
          {settingsTab === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Konfigurišite kako se formiraju cijene za WooCommerce.
              </Alert>

              {/* MPC override - dobavljači čiji feed nosi MPC umjesto nabavne */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff7ed', borderColor: '#fdba74' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mpcOverrideEnabled}
                      onChange={(e) => setMpcOverride(e.target.checked, mpcOverrideList)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        MPC dobavljača kao prodajna cijena
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Za dobavljače čiji feed nosi maloprodajnu umjesto nabavne (DOPER, DIGITALIS). Ako proizvod ima takvog dobavljača, njegov iznos iz supplier_prices ide kao regularna cijena, zaobilazeći markup. Radi i za konsolidovane proizvode.
                      </Typography>
                    </Box>
                  }
                />

                {mpcOverrideEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={availableSuppliers}
                      value={mpcOverrideList.map(s => s.name)}
                      onChange={(_, value) => setMpcOverride(
                        true,
                        value.map(name =>
                          mpcOverrideList.find(s => s.name === name) || { name, onlyWhenSole: false }
                        )
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="MPC dobavljači" placeholder="Odaberi dobavljače" />
                      )}
                    />

                    {mpcOverrideList.length > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {mpcOverrideList.map((s, idx) => (
                          <Box key={s.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" sx={{ minWidth: 130, fontWeight: 600 }}>
                              {idx + 1}. {s.name}
                            </Typography>
                            <TextField
                              select
                              size="small"
                              value={s.onlyWhenSole ? 'sole' : 'priority'}
                              onChange={(e) => setMpcOverride(
                                true,
                                mpcOverrideList.map((x, i) =>
                                  i === idx ? { ...x, onlyWhenSole: e.target.value === 'sole' } : x
                                )
                              )}
                              sx={{ minWidth: 380 }}
                            >
                              <MenuItem value="priority">Prioritet — MPC gazi i tuđe marže</MenuItem>
                              <MenuItem value="sole">Samo kad je jedini dobavljač na proizvodu</MenuItem>
                            </TextField>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                      Redoslijed je prioritet: ako proizvod ima više MPC dobavljača, uzima se prvi iz liste koji ima cijenu.
                      „Prioritet" je za dobavljača bez nabavne u sistemu (DOPER). „Samo kad je jedini" je za dobavljača koji
                      nabavnu i MPC drži u istom polju (DIGITALIS) — tako ne pregazi maržu na tuđim proizvodima.
                    </Typography>

                    {mpcOverrideList.length === 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Nijedan dobavljač nije odabran — override je bez efekta, sve ide na markup.
                      </Alert>
                    )}
                  </Box>
                )}
              </Paper>

              {/* Enable cost price calculation */}
              <FormControlLabel
                control={
                  <Switch
                    checked={pricingConfig?.costPrice?.enabled ?? false}
                    onChange={(e) => setPricingConfig((prev: any) => ({
                      ...prev,
                      costPrice: { ...prev?.costPrice, enabled: e.target.checked }
                    }))}
                  />
                }
                label="Koristi nabavnu cijenu za kalkulaciju"
                sx={{ mb: 2 }}
              />

              {pricingConfig?.costPrice?.enabled && (
                <Grid container spacing={2}>
                  {/* Apply To - Novi/Postojeći/Svi */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Primijeni pricing na:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={pricingConfig?.applyTo?.newProducts ?? true}
                              onChange={(e) => setPricingConfig((prev: any) => ({
                                ...prev,
                                applyTo: { ...prev?.applyTo, newProducts: e.target.checked }
                              }))}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Nove proizvode</Typography>
                              <Typography variant="caption" color="text.secondary">Proizvodi koji se kreiraju</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={pricingConfig?.applyTo?.existingProducts ?? true}
                              onChange={(e) => setPricingConfig((prev: any) => ({
                                ...prev,
                                applyTo: { ...prev?.applyTo, existingProducts: e.target.checked }
                              }))}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Postojeće proizvode</Typography>
                              <Typography variant="caption" color="text.secondary">Proizvodi koji se ažuriraju</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={pricingConfig?.applyTo?.skipUpdatePriceNo ?? false}
                              onChange={(e) => setPricingConfig((prev: any) => ({
                                ...prev,
                                applyTo: { ...prev?.applyTo, skipUpdatePriceNo: e.target.checked }
                              }))}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>Preskoči zaključane cijene</Typography>
                              <Typography variant="caption" color="text.secondary">Proizvodi sa _updateprice = NE</Typography>
                            </Box>
                          }
                        />
                      </Box>
                      {!pricingConfig?.applyTo?.newProducts && !pricingConfig?.applyTo?.existingProducts && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Pricing neće biti primijenjen ni na jednu vrstu proizvoda!
                        </Alert>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info">Nabavna cijena + marža, zatim 17% PDV, pa zaokruživanje. Postojeće konačne cijene ostaju iste. MPC i direktne cijene već su gotove cijene.</Alert>
                  </Grid>
                  {/* Regular Price */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Redovna cijena
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Obračun" value="Marža na nabavnu + 17% PDV" InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={6}>
                    <MarginField
                      fullWidth
                      size="small"
                      type="number"
                      label="Marža bez PDV-a"
                      value={pricingConfig?.regularPriceCalculation?.value || 0}
                      onChange={(e) => setPricingConfig((prev: any) => ({
                        ...prev,
                        regularPriceCalculation: {
                          ...prev?.regularPriceCalculation,
                          value: parseFloat(e.target.value) || 0
                        }
                      }))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">
                          {pricingConfig?.regularPriceCalculation?.type?.includes('percent') ? '%' : ''}
                        </InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={pricingConfig?.regularPriceCalculation?.rounding?.enabled ?? false}
                          onChange={(e) => setPricingConfig((prev: any) => ({
                            ...prev,
                            regularPriceCalculation: {
                              ...prev?.regularPriceCalculation,
                              rounding: { enabled: e.target.checked, type: 'marketing' }
                            }
                          }))}
                          size="small"
                        />
                      }
                      label="Marketing rounding (99, 199, 299...)"
                    />
                  </Grid>

                  {/* Sale Price */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={pricingConfig?.salePriceCalculation?.enabled ?? false}
                          onChange={(e) => setPricingConfig((prev: any) => ({
                            ...prev,
                            salePriceCalculation: {
                              ...prev?.salePriceCalculation,
                              enabled: e.target.checked
                            }
                          }))}
                        />
                      }
                      label="Akcijska cijena"
                    />
                  </Grid>

                  {pricingConfig?.salePriceCalculation?.enabled && (
                    <>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Obračun" value="Marža na nabavnu + 17% PDV" InputProps={{ readOnly: true }} />
                      </Grid>
                      <Grid item xs={6}>
                        <MarginField
                          fullWidth
                          size="small"
                          type="number"
                          label="Marža bez PDV-a"
                          value={pricingConfig?.salePriceCalculation?.value || 0}
                          onChange={(e) => setPricingConfig((prev: any) => ({
                            ...prev,
                            salePriceCalculation: {
                              ...prev?.salePriceCalculation,
                              value: parseFloat(e.target.value) || 0
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={pricingConfig?.salePriceCalculation?.rounding?.enabled ?? false}
                              onChange={(e) => setPricingConfig((prev: any) => ({
                                ...prev,
                                salePriceCalculation: {
                                  ...prev?.salePriceCalculation,
                                  rounding: { enabled: e.target.checked, type: 'marketing' }
                                }
                              }))}
                              size="small"
                            />
                          }
                          label="Marketing rounding (99, 199, 299...)"
                        />
                      </Grid>
                    </>
                  )}

                  {/* ======================================================
                      PRICING PO DOBAVLJAČU
                      ====================================================== */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Pricing pravila po dobavljaču
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const newRule = {
                              supplier: '',
                              pricingMode: 'custom',
                              regularMarkup: 35,
                              saleMarkup: 30,
                              enabled: true,
                            };
                            setPricingConfig((prev: any) => ({
                              ...prev,
                              supplierPriceRules: [...(prev?.supplierPriceRules || []), newRule],
                            }));
                          }}
                        >
                          Dodaj pravilo
                        </Button>
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Ako proizvod ima dobavljača koji ima definirano pravilo, koristi se to pravilo umjesto globalnog.
                      </Typography>

                      {(pricingConfig?.supplierPriceRules || []).length === 0 ? (
                        <Alert severity="info" sx={{ py: 1 }}>
                          Nema definiranih pravila po dobavljaču. Koriste se globalne postavke za sve proizvode.
                        </Alert>
                      ) : (
                        <Box>
                          {(pricingConfig?.supplierPriceRules || []).map((rule: any, idx: number) => (
                            <Paper key={idx} variant="outlined" sx={{ mb: 2, p: 2, bgcolor: rule.enabled ? '#fef9c3' : '#f8fafc' }}>
                              {/* Header row */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Checkbox
                                  checked={rule.enabled ?? true}
                                  onChange={(e) => {
                                    const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                    updated[idx] = { ...updated[idx], enabled: e.target.checked };
                                    setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                  }}
                                  size="small"
                                />
                                <Autocomplete
                                  size="small"
                                  options={availableSuppliers}
                                  value={rule.supplier || ''}
                                  onChange={(_, value) => {
                                    const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                    updated[idx] = { ...updated[idx], supplier: value || '' };
                                    setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                  }}
                                  renderInput={(params) => (
                                    <TextField {...params} placeholder="Dobavljač" sx={{ minWidth: 150 }} />
                                  )}
                                  freeSolo
                                  sx={{ flex: 1 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                  <InputLabel>Mode</InputLabel>
                                  <Select
                                    value={rule.pricingMode || 'custom'}
                                    label="Mode"
                                    onChange={(e) => {
                                      const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                      updated[idx] = { ...updated[idx], pricingMode: e.target.value };
                                      setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                    }}
                                  >
                                    <MenuItem value="custom">Markup %</MenuItem>
                                    <MenuItem value="direct_column">Direktna kolona</MenuItem>
                                  </Select>
                                </FormControl>
                                {(rule.pricingMode || 'custom') !== 'direct_column' && (
                                  <>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Default:</Typography>
                                    <MarginField adjustment={usesPriceAdjustment(rule)}
                                      size="small"
                                      type="number"
                                      value={rule.regularMarkup ?? 0}
                                      onChange={(e) => {
                                        const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                        updated[idx] = { ...updated[idx], regularMarkup: parseFloat(e.target.value) || 0 };
                                        setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                      }}
                                      sx={{ width: 80 }}
                                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                      label={usesPriceAdjustment(rule) ? "Redovna korekcija" : "Redovna marža"}
                                    />
                                    <MarginField adjustment={usesPriceAdjustment(rule)} allowEmpty
                                      size="small"
                                      type="number"
                                      value={rule.saleMarkup ?? ''}
                                      placeholder="prazno = bez sale-a"
                                      onChange={(e) => {
                                        const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                        const raw = e.target.value;
                                        // prazan input → null (sync nece slati sale_price)
                                        // realan broj → parseFloat
                                        updated[idx] = { ...updated[idx], saleMarkup: raw === '' ? null : (parseFloat(raw) || 0) };
                                        setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                      }}
                                      sx={{ width: 110 }}
                                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                      label={usesPriceAdjustment(rule) ? "Akcijska korekcija" : "Akcijska marža"}
                                      helperText={rule.saleMarkup === null || rule.saleMarkup === undefined ? 'bez sale_price' : ''}
                                    />
                                  </>
                                )}
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const updated = (pricingConfig?.supplierPriceRules || []).filter((_: any, i: number) => i !== idx);
                                    setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>

                              {/* Cost price column override for markup mode */}
                              {(rule.pricingMode || 'custom') !== 'direct_column' && (
                                <Box sx={{ ml: 4, mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0', display: 'flex', gap: 2, alignItems: 'center' }}>
                                  <TextField
                                    size="small"
                                    value={rule.costPriceColumn || ''}
                                    onChange={(e) => {
                                      const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                      updated[idx] = { ...updated[idx], costPriceColumn: e.target.value || undefined };
                                      setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                    }}
                                    label="Bazna cijena iz kolone (opciono)"
                                    placeholder="default: nabavna_cijena"
                                    sx={{ minWidth: 250 }}
                                    helperText={usesPriceAdjustment(rule) ? `Korekcija cijene iz ${rule.costPriceColumn}: 0% zadržava cijenu, minus je umanjuje, plus povećava. Bez dodatnog PDV-a.` : "Nabavna cijena + marža, zatim 17% PDV"}
                                  />
                                </Box>
                              )}

                              {/* Direct column fields */}
                              {rule.pricingMode === 'direct_column' && (
                                <Box sx={{ ml: 4, mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <TextField
                                    size="small"
                                    value={rule.directPriceColumn || ''}
                                    onChange={(e) => {
                                      const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                      updated[idx] = { ...updated[idx], directPriceColumn: e.target.value };
                                      setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                    }}
                                    label="Kolona za regularnu cijenu"
                                    placeholder="npr. regular_price"
                                    sx={{ minWidth: 200 }}
                                  />
                                  <TextField
                                    size="small"
                                    value={rule.directSalePriceColumn || ''}
                                    onChange={(e) => {
                                      const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                      updated[idx] = { ...updated[idx], directSalePriceColumn: e.target.value };
                                      setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                    }}
                                    label="Kolona za akcijsku cijenu (opciono)"
                                    placeholder="npr. sale_price"
                                    sx={{ minWidth: 200 }}
                                  />
                                  <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>Zaokruživanje</InputLabel>
                                    <Select
                                      value={rule.directPriceRounding || 'none'}
                                      label="Zaokruživanje"
                                      onChange={(e) => {
                                        const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                        updated[idx] = { ...updated[idx], directPriceRounding: e.target.value };
                                        setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                      }}
                                    >
                                      <MenuItem value="none">Bez zaokruživanja</MenuItem>
                                      <MenuItem value="math">Matematičko (2 dec)</MenuItem>
                                      <MenuItem value="up">Naviše (ceil)</MenuItem>
                                      <MenuItem value="down">Naniže (floor)</MenuItem>
                                      <MenuItem value="marketing">Marketing (99, 199...)</MenuItem>
                                    </Select>
                                  </FormControl>
                                  <Alert severity="info" sx={{ py: 0, flex: '1 1 100%' }}>
                                    Cijena se čita direktno iz navedene Supabase kolone - bez markupa.
                                  </Alert>
                                </Box>
                              )}

                              {/* Price ranges for this supplier */}
                              {(rule.pricingMode || 'custom') !== 'direct_column' && (
                              <Box sx={{ ml: 4, mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Rasponi {usesPriceAdjustment(rule) ? "osnovne cijene" : "nabavne cijene"} za {rule.supplier || 'ovog dobavljača'} (KM):
                                  </Typography>
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                      const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                      const newRange = { minPrice: 0, maxPrice: 100, regularMarkup: 40, saleMarkup: 35, enabled: true };
                                      updated[idx] = { ...updated[idx], priceRanges: [...(rule.priceRanges || []), newRange] };
                                      setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                    }}
                                  >
                                    Dodaj range
                                  </Button>
                                </Box>
                                {(rule.priceRanges || []).length === 0 ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Nema definiranih raspona — koriste se postavke iznad
                                  </Typography>
                                ) : (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ py: 0.5, fontWeight: 600 }}>Od (KM)</TableCell>
                                        <TableCell sx={{ py: 0.5, fontWeight: 600 }}>Do (KM)</TableCell>
                                        <TableCell sx={{ py: 0.5, fontWeight: 600 }}>{usesPriceAdjustment(rule) ? "Redovna korekcija %" : "Redovna marža %"}</TableCell>
                                        <TableCell sx={{ py: 0.5, fontWeight: 600 }}>{usesPriceAdjustment(rule) ? "Akcijska korekcija %" : "Akcijska marža %"}</TableCell>
                                        <TableCell sx={{ py: 0.5, fontWeight: 600 }} align="center">Aktivan</TableCell>
                                        <TableCell sx={{ py: 0.5 }} width={40}></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(rule.priceRanges || []).map((range: any, rangeIdx: number) => (
                                        <TableRow key={rangeIdx}>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <TextField
                                              size="small"
                                              type="number"
                                              value={range.minPrice ?? 0}
                                              onChange={(e) => {
                                                const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                                const ranges = [...(updated[idx].priceRanges || [])];
                                                ranges[rangeIdx] = { ...ranges[rangeIdx], minPrice: parseFloat(e.target.value) || 0 };
                                                updated[idx] = { ...updated[idx], priceRanges: ranges };
                                                setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                              }}
                                              sx={{ width: 70 }}
                                            />
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <TextField
                                              size="small"
                                              type="number"
                                              value={range.maxPrice ?? 100}
                                              onChange={(e) => {
                                                const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                                const ranges = [...(updated[idx].priceRanges || [])];
                                                ranges[rangeIdx] = { ...ranges[rangeIdx], maxPrice: parseFloat(e.target.value) || 0 };
                                                updated[idx] = { ...updated[idx], priceRanges: ranges };
                                                setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                              }}
                                              sx={{ width: 70 }}
                                            />
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <MarginField adjustment={usesPriceAdjustment(rule)}
                                              size="small"
                                              type="number"
                                              value={range.regularMarkup ?? 0}
                                              onChange={(e) => {
                                                const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                                const ranges = [...(updated[idx].priceRanges || [])];
                                                ranges[rangeIdx] = { ...ranges[rangeIdx], regularMarkup: parseFloat(e.target.value) || 0 };
                                                updated[idx] = { ...updated[idx], priceRanges: ranges };
                                                setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                              }}
                                              sx={{ width: 70 }}
                                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                            />
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <MarginField adjustment={usesPriceAdjustment(rule)} allowEmpty
                                              size="small"
                                              type="number"
                                              value={range.saleMarkup ?? ''}
                                              onChange={(e) => {
                                                const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                                const ranges = [...(updated[idx].priceRanges || [])];
                                                ranges[rangeIdx] = { ...ranges[rangeIdx], saleMarkup: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) };
                                                updated[idx] = { ...updated[idx], priceRanges: ranges };
                                                setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                              }}
                                              sx={{ width: 70 }}
                                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                            />
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }} align="center">
                                            <Checkbox
                                              checked={range.enabled ?? true}
                                              onChange={(e) => {
                                                const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                                const ranges = [...(updated[idx].priceRanges || [])];
                                                ranges[rangeIdx] = { ...ranges[rangeIdx], enabled: e.target.checked };
                                                updated[idx] = { ...updated[idx], priceRanges: ranges };
                                                setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                              }}
                                              size="small"
                                            />
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => {
                                                const updated = [...(pricingConfig?.supplierPriceRules || [])];
                                                const ranges = (updated[idx].priceRanges || []).filter((_: any, i: number) => i !== rangeIdx);
                                                updated[idx] = { ...updated[idx], priceRanges: ranges };
                                                setPricingConfig((prev: any) => ({ ...prev, supplierPriceRules: updated }));
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                )}
                              </Box>
                              )}
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* ======================================================
                      PRICING PO RASPONU NABAVNE CIJENE
                      ====================================================== */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Pricing pravila po rasponu nabavne cijene
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const newRule = {
                              minPrice: 0,
                              maxPrice: 100,
                              regularMarkup: 40,
                              saleMarkup: null, // null = bez akcijske cijene
                              useSalePrice: false,
                              roundingType: 'decimal_9', // 'decimal_9' = .90, 'marketing' = 99/199
                              enabled: true,
                            };
                            setPricingConfig((prev: any) => ({
                              ...prev,
                              priceRangeRules: [...(prev?.priceRangeRules || []), newRule],
                            }));
                          }}
                        >
                          Dodaj pravilo
                        </Button>
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Definiši različite markupe i zaokruživanja za različite raspone nabavne cijene. Primjenjuje se prvo pravilo koje odgovara.
                      </Typography>

                      {(pricingConfig?.priceRangeRules || []).length === 0 ? (
                        <Alert severity="info" sx={{ py: 1 }}>
                          Nema definiranih pravila po rasponu. Koriste se globalne postavke za sve cijene.
                        </Alert>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Od (KM)</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Do (KM)</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="center">Redovna marža %</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="center">Akcijska marža %</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Zaokruživanje</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="center">Aktivan</TableCell>
                                <TableCell width={40}></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(pricingConfig?.priceRangeRules || []).map((rule: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={rule.minPrice ?? 0}
                                      onChange={(e) => {
                                        const updated = [...(pricingConfig?.priceRangeRules || [])];
                                        updated[idx] = { ...updated[idx], minPrice: parseFloat(e.target.value) || 0 };
                                        setPricingConfig((prev: any) => ({ ...prev, priceRangeRules: updated }));
                                      }}
                                      sx={{ width: 80 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={rule.maxPrice ?? 100}
                                      onChange={(e) => {
                                        const updated = [...(pricingConfig?.priceRangeRules || [])];
                                        updated[idx] = { ...updated[idx], maxPrice: parseFloat(e.target.value) || 0 };
                                        setPricingConfig((prev: any) => ({ ...prev, priceRangeRules: updated }));
                                      }}
                                      sx={{ width: 80 }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <MarginField
                                      size="small"
                                      type="number"
                                      value={rule.regularMarkup ?? 0}
                                      onChange={(e) => {
                                        const updated = [...(pricingConfig?.priceRangeRules || [])];
                                        updated[idx] = { ...updated[idx], regularMarkup: parseFloat(e.target.value) || 0 };
                                        setPricingConfig((prev: any) => ({ ...prev, priceRangeRules: updated }));
                                      }}
                                      sx={{ width: 70 }}
                                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Checkbox
                                        checked={rule.useSalePrice ?? (rule.saleMarkup !== null && rule.saleMarkup !== undefined)}
                                        onChange={(e) => {
                                          const updated = [...(pricingConfig?.priceRangeRules || [])];
                                          updated[idx] = {
                                            ...updated[idx],
                                            useSalePrice: e.target.checked,
                                            saleMarkup: e.target.checked ? (updated[idx].saleMarkup ?? 30) : null
                                          };
                                          setPricingConfig((prev: any) => ({ ...prev, priceRangeRules: updated }));
                                        }}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                      />
                                      <MarginField allowEmpty
                                        size="small"
                                        type="number"
                                        value={rule.saleMarkup ?? ''}
                                        disabled={!(rule.useSalePrice ?? (rule.saleMarkup !== null && rule.saleMarkup !== undefined))}
                                        onChange={(e) => {
                                          const updated = [...(pricingConfig?.priceRangeRules || [])];
                                          const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                          updated[idx] = { ...updated[idx], saleMarkup: val };
                                          setPricingConfig((prev: any) => ({ ...prev, priceRangeRules: updated }));
                                        }}
                                        sx={{ width: 60 }}
                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                      <Select
                                        value={rule.roundingType || 'marketing'}
                                        onChange={(e) => {
                                          const updated = [...(pricingConfig?.priceRangeRules || [])];
                                          updated[idx] = { ...updated[idx], roundingType: e.target.value };
                                          setPricingConfig((prev: any) => ({ ...prev, priceRangeRules: updated }));
                                        }}
                                      >
                                        <MenuItem value="decimal_9">.90 (49.90, 89.90)</MenuItem>
                                        <MenuItem value="marketing">Marketing (99, 199)</MenuItem>
                                        <MenuItem value="none">Bez zaokruživanja</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Checkbox
                                      checked={rule.enabled ?? true}
                                      onChange={(e) => {
                                        const updated = [...(pricingConfig?.priceRangeRules || [])];
                                        updated[idx] = { ...updated[idx], enabled: e.target.checked };
                                        setPricingConfig((prev: any) => ({ ...prev, priceRangeRules: updated }));
                                      }}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        const updated = (pricingConfig?.priceRangeRules || []).filter((_: any, i: number) => i !== idx);
                                        setPricingConfig((prev: any) => ({ ...prev, priceRangeRules: updated }));
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      <Alert severity="warning" sx={{ mt: 2, py: 0.5 }}>
                        <Typography variant="caption">
                          <strong>Prioritet:</strong> Raspon cijene → Dobavljač → Globalno
                        </Typography>
                      </Alert>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* TAB 2: Filters */}
          {settingsTab === 2 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Filtrirajte koji proizvodi iz Supabase će se sinkronizirati.
              </Alert>

              <Grid container spacing={2}>
                {/* Suppliers */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={availableSuppliers}
                    value={sourceFilters?.suppliers || []}
                    onChange={(_, value) => setSourceFilters((prev: any) => ({
                      ...prev,
                      suppliers: value
                    }))}
                    renderInput={(params) => (
                      <TextField {...params} label="Dobavljači" placeholder="Odaberi dobavljače" />
                    )}
                  />
                </Grid>

                {/* Min Stock */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Minimalna količina"
                    value={sourceFilters?.min_stock_quantity || 0}
                    onChange={(e) => setSourceFilters((prev: any) => ({
                      ...prev,
                      min_stock_quantity: parseInt(e.target.value) || 0
                    }))}
                  />
                </Grid>

                {/* Product Status */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Status proizvoda</InputLabel>
                    <Select
                      multiple
                      value={sourceFilters?.product_status || []}
                      label="Status proizvoda"
                      onChange={(e) => setSourceFilters((prev: any) => ({
                        ...prev,
                        product_status: e.target.value
                      }))}
                    >
                      <MenuItem value="publish">Objavljeno</MenuItem>
                      <MenuItem value="draft">Nacrt</MenuItem>
                      <MenuItem value="pending">Na čekanju</MenuItem>
                      <MenuItem value="private">Privatno</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Included Categories (whitelist) */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={
                      (sourceFilters?.category_custom_field_mappings || [])
                        .filter((m: any) => m.targetId)
                        .map((m: any) => ({ id: m.targetId, name: m.targetName || `ID: ${m.targetId}` }))
                        .filter((v: any, i: number, a: any[]) => a.findIndex((t: any) => t.id === v.id) === i)
                        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
                    }
                    value={
                      (sourceFilters?.included_category_ids || []).map((id: number) => {
                        const mapping = (sourceFilters?.category_custom_field_mappings || []).find((m: any) => m.targetId === id);
                        return { id, name: mapping?.targetName || `ID: ${id}` };
                      })
                    }
                    onChange={(_, newValue) => setSourceFilters((prev: any) => ({
                      ...prev,
                      included_category_ids: newValue.map((v: any) => v.id)
                    }))}
                    getOptionLabel={(option: any) => `${option.name} (ID: ${option.id})`}
                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Uključene kategorije"
                        helperText="Ako je popunjeno, sinkronizirat će se SAMO proizvodi koji se mapiraju u ove kategorije. Ostavi prazno za sve."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option: any, index: number) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.name || `ID: ${option.id}`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))
                    }
                  />
                </Grid>

                {/* Excluded Categories */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={
                      (sourceFilters?.category_custom_field_mappings || [])
                        .filter((m: any) => m.targetId)
                        .map((m: any) => ({ id: m.targetId, name: m.targetName || `ID: ${m.targetId}` }))
                        .filter((v: any, i: number, a: any[]) => a.findIndex((t: any) => t.id === v.id) === i)
                        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
                    }
                    value={
                      (sourceFilters?.excluded_category_ids || []).map((id: number) => {
                        const mapping = (sourceFilters?.category_custom_field_mappings || []).find((m: any) => m.targetId === id);
                        return { id, name: mapping?.targetName || `ID: ${id}` };
                      })
                    }
                    onChange={(_, newValue) => setSourceFilters((prev: any) => ({
                      ...prev,
                      excluded_category_ids: newValue.map((v: any) => v.id)
                    }))}
                    getOptionLabel={(option: any) => `${option.name} (ID: ${option.id})`}
                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Isključene kategorije"
                        helperText="Proizvodi koji se mapiraju u ove kategorije neće biti importovani."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option: any, index: number) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.name || `ID: ${option.id}`}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      ))
                    }
                  />
                </Grid>
              </Grid>

              {/* Per-Supplier Filters Section */}
              {(sourceFilters?.suppliers?.length > 0) && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterIcon fontSize="small" color="primary" />
                    Napredni filteri po dobavljaču
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Definirajte specifične filtere za svakog dobavljača. Ako nije definiran specifičan filter, koristit će se globalni filteri iznad.
                  </Alert>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Dobavljač</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Min. zaliha</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Min. cijena</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Kategorije i brendovi
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
                              Prazno = sve kategorije. Po odabranoj kategoriji možeš suziti na određene brendove. Ispod možeš isključiti pojedine kategorije.
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Image Proxy URL</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Aktivan</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(sourceFilters?.suppliers || []).map((supplier: string) => {
                          const supplierFilter = (sourceFilters?.supplier_filters || []).find(
                            (sf: SupplierFilter) => sf.supplier === supplier
                          ) || {
                            supplier,
                            enabled: true,
                            min_stock_quantity: sourceFilters?.min_stock_quantity || 0,
                            min_price: 0,
                            categories: []
                          };

                          const updateSupplierFilter = (field: keyof SupplierFilter, value: any) => {
                            setSourceFilters((prev: any) => {
                              const existingFilters: SupplierFilter[] = prev.supplier_filters || [];
                              const existingIndex = existingFilters.findIndex(sf => sf.supplier === supplier);

                              let newFilters: SupplierFilter[];
                              if (existingIndex >= 0) {
                                newFilters = [...existingFilters];
                                newFilters[existingIndex] = { ...newFilters[existingIndex], [field]: value };
                              } else {
                                newFilters = [...existingFilters, {
                                  supplier,
                                  enabled: true,
                                  min_stock_quantity: prev.min_stock_quantity || 0,
                                  min_price: 0,
                                  categories: [],
                                  [field]: value
                                }];
                              }

                              return { ...prev, supplier_filters: newFilters };
                            });
                          };

                          return (
                            <TableRow key={supplier}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {supplier}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={supplierFilter.min_stock_quantity}
                                  onChange={(e) => updateSupplierFilter('min_stock_quantity', parseInt(e.target.value) || 0)}
                                  sx={{ width: 80 }}
                                  inputProps={{ min: 0 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={supplierFilter.min_price || 0}
                                  onChange={(e) => updateSupplierFilter('min_price', parseFloat(e.target.value) || 0)}
                                  sx={{ width: 100 }}
                                  inputProps={{ min: 0, step: '0.01' }}
                                />
                              </TableCell>
                              <TableCell>
                                <SupplierCategoriesFilter
                                  shopId={editingShop?.id}
                                  supplier={supplier}
                                  value={supplierFilter.categories || []}
                                  onChange={(cats) => {
                                    updateSupplierFilter('categories', cats);
                                    // Kategorija uklonjena → ukloni i njena ograničenja brendova,
                                    // inače bi tiho oživjela ako se kategorija kasnije vrati
                                    const current = supplierFilter.category_brands || {};
                                    const kept = Object.fromEntries(
                                      Object.entries(current).filter(([cat]) => cats.includes(cat))
                                    );
                                    if (Object.keys(kept).length !== Object.keys(current).length) {
                                      updateSupplierFilter('category_brands', kept);
                                    }
                                  }}
                                />
                                <SupplierCategoryBrands
                                  supplier={supplier}
                                  categories={supplierFilter.categories || []}
                                  value={supplierFilter.category_brands || {}}
                                  onChange={(next) => updateSupplierFilter('category_brands', next)}
                                />
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    Isključi kategorije (uvijek pobjeđuje listu iznad)
                                  </Typography>
                                  <SupplierCategoriesFilter
                                    shopId={editingShop?.id}
                                    supplier={supplier}
                                    value={supplierFilter.excluded_categories || []}
                                    onChange={(cats) => updateSupplierFilter('excluded_categories', cats)}
                                    placeholder="Ništa nije isključeno"
                                    chipColor="error"
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  value={supplierFilter.image_proxy_url || ''}
                                  onChange={(e) => updateSupplierFilter('image_proxy_url', e.target.value)}
                                  placeholder="https://proxy.com?img={url}"
                                  sx={{ width: 280 }}
                                  helperText={supplierFilter.image_proxy_url ? '{url} = originalni URL slike' : ''}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Switch
                                  checked={supplierFilter.enabled}
                                  onChange={(e) => updateSupplierFilter('enabled', e.target.checked)}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>

                  <Alert severity="warning" sx={{ mt: 2, py: 0.5 }}>
                    <Typography variant="caption">
                      <strong>Primjer:</strong> ASBIS min. zaliha = 10 znači da će se sinkronizirati samo ASBIS proizvodi sa zalihom &gt;= 10
                    </Typography>
                  </Alert>
                </Box>
              )}

            </Box>
          )}

          {/* TAB 3: Categories */}
          {settingsTab === 3 && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={sourceFilters?.category_mapping_enabled ?? false}
                    onChange={(e) => setSourceFilters((prev: any) => ({
                      ...prev,
                      category_mapping_enabled: e.target.checked
                    }))}
                  />
                }
                label="Omogući mapiranje kategorija"
                sx={{ mb: 2 }}
              />

              {sourceFilters?.category_mapping_enabled && (
                <>
                  {/* Default kategorija za nemapirane */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Default kategorija (za nemapirane)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Proizvodi bez mapiranja idu u ovu kategoriju. Ostavi prazno za fallback "Nema Kategoriju" (ID 73).
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        label="ID"
                        type="number"
                        value={sourceFilters?.default_category_id || ''}
                        onChange={(e) => setSourceFilters((prev: any) => ({
                          ...prev,
                          default_category_id: e.target.value ? parseInt(e.target.value) : null
                        }))}
                        sx={{ width: 100 }}
                      />
                      <TextField
                        size="small"
                        label="Naziv"
                        value={sourceFilters?.default_category_name || ''}
                        onChange={(e) => setSourceFilters((prev: any) => ({
                          ...prev,
                          default_category_name: e.target.value || null
                        }))}
                        sx={{ flex: 1, maxWidth: 300 }}
                        placeholder="npr. Nekategorizirano"
                      />
                      {sourceFilters?.default_category_id && (
                        <Chip
                          size="small"
                          label={`${sourceFilters.default_category_name || 'Default'} (ID: ${sourceFilters.default_category_id})`}
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Paper>

                <Grid container spacing={2}>
                  {/* LIJEVA STRANA: Supabase kategorije */}
                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, height: 450 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CategoryIcon fontSize="small" color="primary" />
                          Supabase kategorije
                        </Typography>
                        <Tooltip title="Osvježi kategorije">
                          <IconButton size="small" onClick={() => loadSupabaseCategories(editingShop?.id)} disabled={loadingCategories}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Pretraga i filter */}
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Pretraži kategorije..."
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        sx={{ mb: 1 }}
                        InputProps={{
                          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={showOnlyUnmapped}
                            onChange={(e) => setShowOnlyUnmapped(e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">Samo nemapirane</Typography>}
                        sx={{ mb: 1 }}
                      />

                      {/* Statistike */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip
                          size="small"
                          icon={<MappedIcon />}
                          label={`Mapirano: ${supabaseCategories.filter(c => isCategoryMapped(c)).length}`}
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          icon={<UnmappedIcon />}
                          label={`Nemapirano: ${supabaseCategories.filter(c => !isCategoryMapped(c)).length}`}
                          color="warning"
                          variant="outlined"
                        />
                      </Box>

                      {/* Lista kategorija */}
                      {loadingCategories ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <List
                          dense
                          sx={{
                            maxHeight: 270,
                            overflow: 'auto',
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                          }}
                        >
                          {getFilteredCategories().length === 0 ? (
                            <ListItem>
                              <ListItemText
                                primary="Nema kategorija"
                                secondary={categorySearch ? 'Probaj drugu pretragu' : 'Učitaj kategorije'}
                              />
                            </ListItem>
                          ) : (
                            getFilteredCategories().map((cat) => {
                              const mapped = isCategoryMapped(cat);
                              const mapping = getMappingForCategory(cat);
                              return (
                                <ListItem
                                  key={cat}
                                  dense
                                  button={mapped}
                                  onClick={mapped ? () => startEditMapping(cat) : undefined}
                                  secondaryAction={
                                    !mapped ? (
                                      <Tooltip title="Dodaj mapiranje">
                                        <IconButton
                                          edge="end"
                                          size="small"
                                          onClick={() => setNewMapping(prev => ({ ...prev, sourceValue: cat }))}
                                        >
                                          <AddIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title={`Klikni za edit → ${mapping?.targetName || mapping?.targetId}`}>
                                        <Chip size="small" label={mapping?.targetId} color="success" />
                                      </Tooltip>
                                    )
                                  }
                                  sx={{
                                    bgcolor: mapped ? 'success.50' : 'transparent',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    cursor: mapped ? 'pointer' : 'default',
                                    '&:hover': mapped ? { bgcolor: 'success.100' } : {},
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    {mapped ? (
                                      <MappedIcon fontSize="small" color="success" />
                                    ) : (
                                      <UnmappedIcon fontSize="small" color="warning" />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={cat}
                                    secondary={mapped ? `→ ${mapping?.targetName || 'ID: ' + mapping?.targetId}` : undefined}
                                    primaryTypographyProps={{
                                      variant: 'body2',
                                      sx: { fontWeight: mapped ? 500 : 400 },
                                    }}
                                    secondaryTypographyProps={{
                                      variant: 'caption',
                                      sx: { color: 'success.main' },
                                    }}
                                  />
                                </ListItem>
                              );
                            })
                          )}
                        </List>
                      )}
                    </Paper>
                  </Grid>

                  {/* SREDINA: Dodaj/Edituj mapiranje */}
                  <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Paper variant="outlined" sx={{ p: 2, width: '100%', bgcolor: editingMapping ? 'warning.50' : 'inherit' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, mb: 2, display: 'block', textAlign: 'center', color: editingMapping ? 'warning.main' : 'inherit' }}>
                        {editingMapping ? 'EDITUJ MAPIRANJE' : 'NOVO MAPIRANJE'}
                      </Typography>

                      <TextField
                        fullWidth
                        size="small"
                        label="Source"
                        value={newMapping.sourceValue}
                        onChange={(e) => setNewMapping(prev => ({ ...prev, sourceValue: e.target.value }))}
                        sx={{ mb: 1 }}
                        disabled={!!editingMapping}
                      />

                      <TextField
                        fullWidth
                        size="small"
                        label="Target ID"
                        type="number"
                        value={newMapping.targetId}
                        onChange={(e) => setNewMapping(prev => ({ ...prev, targetId: e.target.value }))}
                        sx={{ mb: 1 }}
                      />

                      <TextField
                        fullWidth
                        size="small"
                        label="Target naziv (opciono)"
                        value={newMapping.targetName}
                        onChange={(e) => setNewMapping(prev => ({ ...prev, targetName: e.target.value }))}
                        sx={{ mb: 2 }}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        color={editingMapping ? 'warning' : 'primary'}
                        startIcon={editingMapping ? <EditIcon /> : <AddIcon />}
                        onClick={addCategoryMapping}
                        disabled={!newMapping.sourceValue || !newMapping.targetId}
                      >
                        {editingMapping ? 'Sačuvaj' : 'Dodaj'}
                      </Button>
                      {editingMapping && (
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={cancelEditMapping}
                        >
                          Otkaži
                        </Button>
                      )}
                    </Paper>
                  </Grid>

                  {/* DESNA STRANA: Aktivna mapiranja */}
                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, height: 450 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ArrowIcon fontSize="small" color="primary" />
                        Aktivna mapiranja ({sourceFilters?.category_custom_field_mappings?.length || 0})
                      </Typography>

                      {/* Pretraga mapiranja */}
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Pretraži mapiranja..."
                        value={mappingSearch}
                        onChange={(e) => setMappingSearch(e.target.value)}
                        sx={{ mb: 1 }}
                        InputProps={{
                          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />

                      {sourceFilters?.category_custom_field_mappings?.length > 0 ? (
                        <TableContainer sx={{ maxHeight: 330 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Naziv</TableCell>
                                <TableCell width={70}></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sourceFilters.category_custom_field_mappings
                                .filter((mapping: any) => {
                                  if (!mappingSearch) return true;
                                  const src = (mapping.sourceValue || mapping.sourceFieldValue || '').toLowerCase();
                                  const tgt = (mapping.targetName || '').toLowerCase();
                                  const search = mappingSearch.toLowerCase();
                                  return src.includes(search) || tgt.includes(search);
                                })
                                .map((mapping: any, idx: number) => (
                                <TableRow key={idx} hover>
                                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {mapping.sourceValue || mapping.sourceFieldValue}
                                  </TableCell>
                                  <TableCell>{mapping.targetId}</TableCell>
                                  <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {mapping.targetName || '-'}
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="Edituj">
                                      <IconButton
                                        size="small"
                                        onClick={() => startEditMapping(mapping.sourceValue || mapping.sourceFieldValue)}
                                        color="primary"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Obriši">
                                      <IconButton
                                        size="small"
                                        onClick={() => removeCategoryMapping(mapping.sourceValue || mapping.sourceFieldValue)}
                                        color="error"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                          <UnmappedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Nema definiranih mapiranja
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Odaberi kategoriju s lijeve strane
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
                </>
              )}
            </Box>
          )}

          {/* TAB 4: Attribute Mapping */}
          {settingsTab === 4 && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={sourceFilters?.attribute_mappings?.enabled ?? false}
                    onChange={(e) => setSourceFilters((prev: any) => ({
                      ...prev,
                      attribute_mappings: {
                        ...prev?.attribute_mappings,
                        enabled: e.target.checked,
                        mappings: prev?.attribute_mappings?.mappings || [],
                        unmappedBehavior: prev?.attribute_mappings?.unmappedBehavior || 'keep_original',
                      }
                    }))}
                  />
                }
                label="Omogući mapiranje atributa (specifikacije)"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Mapira nazive iz Supabase <code>specifikacije</code> kolone na WooCommerce atribute.
              </Typography>

              {/* Unmapped behavior */}
              <FormControl size="small" sx={{ mb: 3, minWidth: 250 }}>
                <InputLabel>Nemapirani atributi</InputLabel>
                <Select
                  value={sourceFilters?.attribute_mappings?.unmappedBehavior || 'keep_original'}
                  label="Nemapirani atributi"
                  onChange={(e) => setSourceFilters((prev: any) => ({
                    ...prev,
                    attribute_mappings: {
                      ...prev?.attribute_mappings,
                      unmappedBehavior: e.target.value,
                    }
                  }))}
                >
                  <MenuItem value="keep_original">Zadrži originalni naziv</MenuItem>
                  <MenuItem value="skip">Preskoči (ne sync)</MenuItem>
                </Select>
              </FormControl>

              {/* Lista atributa - uvijek prikazana kad je tab aktivan */}
              <Grid container spacing={2}>
                  {/* LIJEVA STRANA: Supabase specifikacije */}
                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, height: 400 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SettingsIcon fontSize="small" color="primary" />
                          Supabase specifikacije
                        </Typography>
                        <Tooltip title="Osvježi atribute">
                          <IconButton size="small" onClick={loadSupabaseAttributes} disabled={loadingAttributes}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Pretraga */}
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Pretraži atribute..."
                        value={attributeSearch}
                        onChange={(e) => setAttributeSearch(e.target.value)}
                        sx={{ mb: 1 }}
                        InputProps={{
                          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />

                      {/* Statistike */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip
                          size="small"
                          icon={<MappedIcon />}
                          label={`Mapirano: ${supabaseAttributes.filter(a => isAttributeMapped(a)).length}`}
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          icon={<UnmappedIcon />}
                          label={`Nemapirano: ${supabaseAttributes.filter(a => !isAttributeMapped(a)).length}`}
                          color="warning"
                          variant="outlined"
                        />
                      </Box>

                      {/* Lista atributa */}
                      {loadingAttributes ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : supabaseAttributes.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            Klikni "Osvježi" za učitavanje atributa
                          </Typography>
                        </Box>
                      ) : (
                        <List
                          dense
                          sx={{
                            maxHeight: 220,
                            overflow: 'auto',
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                          }}
                        >
                          {getFilteredAttributes().map((attr) => {
                            const isMapped = isAttributeMapped(attr);
                            const isSelected = selectedAttribute === attr;
                            return (
                              <ListItem
                                key={attr}
                                onClick={() => {
                                  if (!isMapped) {
                                    setSelectedAttribute(attr);
                                    setTargetAttributeName(attr); // Default: isti naziv
                                  }
                                }}
                                sx={{
                                  cursor: isMapped ? 'default' : 'pointer',
                                  bgcolor: isSelected ? 'primary.light' : isMapped ? 'success.light' : 'transparent',
                                  '&:hover': {
                                    bgcolor: isMapped ? 'success.light' : isSelected ? 'primary.light' : 'action.hover',
                                  },
                                  borderRadius: 1,
                                  mb: 0.5,
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  {isMapped ? (
                                    <MappedIcon fontSize="small" color="success" />
                                  ) : (
                                    <UnmappedIcon fontSize="small" color="warning" />
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={attr}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    sx: { fontWeight: isSelected ? 600 : 400 }
                                  }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      )}
                    </Paper>
                  </Grid>

                  {/* SREDINA: Mapiranje */}
                  <Grid item xs={12} md={2} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    {selectedAttribute && (
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Source:</Typography>
                        <Chip label={selectedAttribute} size="small" color="warning" sx={{ mb: 1 }} />

                        <ArrowIcon sx={{ display: 'block', mx: 'auto', my: 1, transform: 'rotate(90deg)' }} />

                        <Typography variant="caption" color="text.secondary">Target:</Typography>
                        <TextField
                          size="small"
                          value={targetAttributeName}
                          onChange={(e) => setTargetAttributeName(e.target.value)}
                          placeholder="Naziv u WooCommerce"
                          sx={{ mb: 1, width: '100%' }}
                        />

                        <Button
                          variant="contained"
                          size="small"
                          onClick={addSingleAttributeMapping}
                          startIcon={<AddIcon />}
                          fullWidth
                        >
                          Dodaj
                        </Button>
                      </Box>
                    )}
                    {!selectedAttribute && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Odaberi atribut s lijeve strane
                      </Typography>
                    )}
                  </Grid>

                  {/* DESNA STRANA: Aktivna mapiranja */}
                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, height: 400 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Aktivna mapiranja ({sourceFilters?.attribute_mappings?.mappings?.length || 0})
                        </Typography>
                        {sourceFilters?.attribute_mappings?.mappings?.length > 0 && (
                          <Button
                            size="small"
                            color="error"
                            onClick={clearAllAttributeMappings}
                            startIcon={<DeleteIcon />}
                          >
                            Obriši sve
                          </Button>
                        )}
                      </Box>

                      {sourceFilters?.attribute_mappings?.mappings?.length > 0 ? (
                        <TableContainer sx={{ maxHeight: 320 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Source (Supabase)</TableCell>
                                <TableCell>Target (WooCommerce)</TableCell>
                                <TableCell width={50}></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sourceFilters.attribute_mappings.mappings.map((m: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell>{m.sourceName}</TableCell>
                                  <TableCell>{m.targetName}</TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => removeAttributeMapping(m.sourceName)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                          <UnmappedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Nema definiranih mapiranja
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>

              <Divider sx={{ my: 2 }} />

              {/* JSON Editor */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography variant="subtitle2">Bulk import putem JSON-a</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Format: [{"{"}"sourceName": "Boja", "targetName": "pa_color"{"}"}, ...]
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    placeholder={`[
  { "sourceName": "Boja", "targetName": "pa_color" },
  { "sourceName": "Rezolucija", "targetName": "pa_resolution" }
]`}
                    value={attributeMappingJson}
                    onChange={(e) => {
                      setAttributeMappingJson(e.target.value);
                      setAttributeMappingError(null);
                    }}
                    error={!!attributeMappingError}
                    helperText={attributeMappingError}
                    sx={{ mb: 2, fontFamily: 'monospace' }}
                  />

                  <Button
                    variant="contained"
                    onClick={applyAttributeMappingJson}
                    disabled={!attributeMappingJson.trim()}
                    startIcon={<AddIcon />}
                  >
                    Primijeni JSON
                  </Button>
                </AccordionDetails>
              </Accordion>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Napomena:</strong> Mapiranje se primjenjuje samo kada je "Atributi" uključen u Sync Polja (tab 1).
                </Typography>
              </Alert>
            </Box>
          )}

          {/* TAB 5: Name Rules - Pravila naziva po dobavljaču */}
          {settingsTab === 5 && (
            <Box>
              <NameRulesPanel
                config={nameRulesConfig}
                onChange={setNameRulesConfig}
                availableSuppliers={availableSuppliers}
              />
            </Box>
          )}

          {/* TAB 6: Custom Fields - Mapiranje Supabase polja na WooCommerce custom fields */}
          {settingsTab === 6 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Mapirajte polja iz Supabase na custom fields u WooCommerce-u.
                Custom fields se čuvaju kao meta_data na proizvodu.
              </Alert>

              {/* Opcija za primjenu */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Primijeni na:
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Odaberite za koje proizvode će se primjenjivati custom field mapiranja
                </Typography>
              </Paper>

              {/* Dodavanje novog mapiranja */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Dodaj novo mapiranje
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Supabase polje</InputLabel>
                      <Select
                        value=""
                        label="Supabase polje"
                        onChange={(e) => {
                          const sourceField = e.target.value as string;
                          if (sourceField && !customFieldMappings.find(m => m.sourceField === sourceField)) {
                            setCustomFieldMappings(prev => [...prev, {
                              sourceField,
                              targetField: `_${sourceField}`,
                              applyTo: 'all',
                              enabled: true
                            }]);
                          }
                        }}
                      >
                        {SUPABASE_PRODUCT_FIELDS
                          .filter(f => !customFieldMappings.find(m => m.sourceField === f.key))
                          .map(field => (
                            <MenuItem key={field.key} value={field.key}>
                              {field.label} ({field.key})
                            </MenuItem>
                          ))
                        }
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography variant="caption" color="text.secondary">
                      Odaberite Supabase polje sa lijeve strane. Target field možete urediti u tabeli ispod.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Lista mapiranja */}
              {customFieldMappings.length === 0 ? (
                <Alert severity="warning">
                  Nema definiranih custom field mapiranja. Dodajte mapiranje iznad.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Supabase polje</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>WooCommerce custom field</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Primijeni na</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Aktivan</TableCell>
                        <TableCell width={50}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customFieldMappings.map((mapping, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DatabaseIcon fontSize="small" color="primary" />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {SUPABASE_PRODUCT_FIELDS.find(f => f.key === mapping.sourceField)?.label || mapping.sourceField}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {mapping.sourceField}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={mapping.targetField}
                              onChange={(e) => {
                                const updated = [...customFieldMappings];
                                updated[idx] = { ...updated[idx], targetField: e.target.value };
                                setCustomFieldMappings(updated);
                              }}
                              placeholder="_custom_field"
                              fullWidth
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <WooIcon fontSize="small" color="secondary" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={mapping.applyTo}
                                onChange={(e) => {
                                  const updated = [...customFieldMappings];
                                  updated[idx] = { ...updated[idx], applyTo: e.target.value as 'all' | 'new' | 'existing' };
                                  setCustomFieldMappings(updated);
                                }}
                              >
                                <MenuItem value="all">Sve proizvode</MenuItem>
                                <MenuItem value="new">Samo nove</MenuItem>
                                <MenuItem value="existing">Samo postojeće</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={mapping.enabled}
                              onChange={(e) => {
                                const updated = [...customFieldMappings];
                                updated[idx] = { ...updated[idx], enabled: e.target.checked };
                                setCustomFieldMappings(updated);
                              }}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setCustomFieldMappings(prev => prev.filter((_, i) => i !== idx));
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Info o korištenju */}
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Primjer:</strong> Ako mapirate <code>ean</code> → <code>_ean</code>, vrijednost EAN koda iz Supabase će biti dostupna u WooCommerce-u kao custom field <code>_ean</code>.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* TAB 7: Webhook Trigger - Polja koja triggeruju automatski sync */}
          {settingsTab === 7 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Delta Sync</strong> - Odaberite polja koja želite pratiti. Kada se neko od odabranih polja promijeni u Supabase bazi,
                  proizvod će automatski biti označen za sinkronizaciju. Cron job sa <code>useNeedsSync=true</code> parametrom će
                  sinkronizirati samo te proizvode umjesto svih.
                </Typography>
              </Alert>

              {/* API Endpoint primjeri */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.900' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'grey.100' }}>
                  🔗 API Endpoint za ovaj shop
                </Typography>

                <Typography variant="caption" sx={{ color: 'success.light', fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Full Sync (plugin mode):
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.800',
                    p: 1.5,
                    borderRadius: 1,
                    mb: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'grey.300',
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <code className={styles.endpointVerb}>GET</code>{' '}
                  <code>/api/sync/full-sync?shopId=<span className={styles.endpointParamShopId}>{editingShop?.id}</span>&secret=<span className={styles.endpointParamSecret}>YOUR_CRON_SECRET</span>&usePlugin=<span className={styles.endpointParamFlag}>true</span></code>
                </Box>

                <Typography variant="caption" sx={{ color: 'warning.light', fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Full Sync + Delete (draft neaktivne):
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.800',
                    p: 1.5,
                    borderRadius: 1,
                    mb: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'grey.300',
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <code className={styles.endpointVerb}>GET</code>{' '}
                  <code>/api/sync/full-sync?shopId=<span className={styles.endpointParamShopId}>{editingShop?.id}</span>&secret=<span className={styles.endpointParamSecret}>YOUR_CRON_SECRET</span>&usePlugin=<span className={styles.endpointParamFlag}>true</span>&deleteAction=<span className={styles.endpointParamFlag}>draft</span></code>
                </Box>

                <Typography variant="caption" sx={{ color: 'info.light', fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Dry Run (pregled bez promjena):
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.800',
                    p: 1.5,
                    borderRadius: 1,
                    mb: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'grey.300',
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <code className={styles.endpointVerb}>GET</code>{' '}
                  <code>/api/sync/full-sync?shopId=<span className={styles.endpointParamShopId}>{editingShop?.id}</span>&secret=<span className={styles.endpointParamSecret}>YOUR_CRON_SECRET</span>&usePlugin=<span className={styles.endpointParamFlag}>true</span>&dryRun=<span className={styles.endpointParamFlag}>true</span></code>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  <strong>Parametri:</strong><br />
                  • <code>shopId</code> - ID ovog shopa (obavezno)<br />
                  • <code>secret</code> - CRON_SECRET iz environment varijabli (obavezno)<br />
                  • <code>usePlugin=true</code> - koristi plugin za brzi sync (preporučeno)<br />
                  • <code>deleteAction</code> - draft, trash ili none (default: draft)<br />
                  • <code>dryRun=true</code> - simulacija bez promjena<br />
                  • <code>force=true</code> - forsiraj update svih proizvoda<br />
                  • <code>chunkSize</code> - broj proizvoda po chunk-u (default: 400)
                </Typography>
              </Paper>

              {/* Delta Sync Status */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <QueueIcon color="primary" />
                    Delta Sync Status
                  </Typography>
                  <Button
                    size="small"
                    startIcon={loadingQueueStats ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={() => editingShop && loadSyncQueueStats(editingShop.id!)}
                    disabled={loadingQueueStats}
                  >
                    Osvježi
                  </Button>
                </Box>
                {syncQueueStats ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'warning.50', borderRadius: 1 }}>
                        <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                          {syncQueueStats.pending}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Čeka sinkronizaciju</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'success.50', borderRadius: 1 }}>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                          {syncQueueStats.done}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Sinkronizirano</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nema podataka - delta sync još nije korišten za ovaj shop
                  </Typography>
                )}
              </Paper>

              {/* Odabir trigger polja */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Polja koja triggeruju webhook
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Kada se neko od ovih polja promijeni u Supabase bazi, proizvod će automatski biti dodan u queue za sinkronizaciju.
                </Typography>

                {/* Dostupna polja za odabir */}
                <Grid container spacing={1}>
                  {[
                    { key: 'nabavna_cijena', label: 'Nabavna cijena', desc: 'Promjena nabavne cijene' },
                    { key: 'stock_quantity', label: 'Količina', desc: 'Promjena zalihe' },
                    { key: 'name', label: 'Naziv', desc: 'Promjena naziva proizvoda' },
                    { key: 'description', label: 'Opis', desc: 'Promjena opisa' },
                    { key: 'short_description', label: 'Kratki opis', desc: 'Promjena kratkog opisa' },
                    { key: 'regular_price', label: 'Redovna cijena', desc: 'Promjena prodajne cijene' },
                    { key: 'sale_price', label: 'Akcijska cijena', desc: 'Promjena akcijske cijene' },
                    { key: 'images', label: 'Slike', desc: 'Promjena slika (dodavanje/brisanje)' },
                    { key: 'categories', label: 'Kategorije', desc: 'Promjena kategorija' },
                    { key: 'brand', label: 'Brend', desc: 'Promjena brenda' },
                    { key: 'ean', label: 'EAN', desc: 'Promjena EAN koda' },
                    { key: 'garancija', label: 'Garancija', desc: 'Promjena garancije' },
                    { key: 'dobavljac', label: 'Dobavljač', desc: 'Promjena glavnog dobavljača' },
                    { key: 'supplier_stocks', label: 'Zalihe po dobavljačima', desc: 'Promjena zaliha bilo kojeg dobavljača' },
                    { key: 'specifikacije', label: 'Specifikacije', desc: 'Promjena specifikacija/atributa' },
                    { key: 'status', label: 'Status', desc: 'Promjena statusa (publish/draft)' },
                  ].map(field => {
                    const isSelected = webhookTriggerFields[field.key] === true;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={field.key}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            bgcolor: isSelected ? 'primary.50' : 'transparent',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            borderWidth: isSelected ? 2 : 1,
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: isSelected ? 'primary.100' : 'grey.50',
                            },
                          }}
                          onClick={() => {
                            setWebhookTriggerFields(prev => ({
                              ...prev,
                              [field.key]: !prev[field.key]
                            }));
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              sx={{ p: 0 }}
                            />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                {field.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {field.desc}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>

              {/* Info kako radi */}
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Kako radi Delta Sync:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  <ol className={styles.deltaSyncOl}>
                    <li>Kada se ažurira proizvod u Supabase, PostgreSQL trigger provjerava promjene odabranih polja</li>
                    <li>Ako se promijenilo neko od praćenih polja, postavlja <code>needs_sync=true</code> u <strong>product_sync_status</strong> tablici za ovaj shop</li>
                    <li>Cron job sa <code>useNeedsSync=true</code> sinkronizira samo proizvode gdje je <code>needs_sync=true</code></li>
                    <li>Nakon uspješne sinkronizacije, <code>needs_sync</code> se resetira na <code>false</code></li>
                  </ol>
                </Typography>
              </Alert>

              {(() => {
                const activeFields = Object.entries(webhookTriggerFields).filter(([_, v]) => v).map(([k]) => k);
                return (
                  <>
                    {activeFields.length === 0 && (
                      <Alert severity="warning">
                        Niste odabrali nijedno polje. Webhook trigger je <strong>isključen</strong> za ovaj shop.
                        Proizvodi će se sinkronizirati samo putem cron-a ili manualnog sync-a.
                      </Alert>
                    )}

                    {activeFields.length > 0 && (
                      <Alert severity="info">
                        <Typography variant="body2">
                          <strong>Aktivna polja ({activeFields.length}):</strong>{' '}
                          {activeFields.join(', ')}
                        </Typography>
                      </Alert>
                    )}
                  </>
                );
              })()}
            </Box>
          )}

          {/* TAB 8: SejkanSync Plugin */}
          {settingsTab === 8 && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>SejkanSync Plugin</strong> omogućava 5-10x brži sync korištenjem WordPress Action Scheduler-a.
                  Umjesto WooCommerce REST API (sporo), plugin koristi direktne WooCommerce CRUD metode.
                </Typography>
              </Alert>

              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PluginIcon color="primary" />
                  Konfiguracija Plugina
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={pluginConfig.enabled}
                      onChange={(e) => setPluginConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Koristi SejkanSync Plugin
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Omogući brzi sync preko WordPress plugina umjesto REST API-ja
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
                />

                {pluginConfig.enabled && (
                  <Box sx={{ ml: 4, mt: 2 }}>
                    <TextField
                      label="Plugin API Key"
                      value={pluginConfig.api_key}
                      onChange={(e) => setPluginConfig(prev => ({ ...prev, api_key: e.target.value }))}
                      fullWidth
                      placeholder="zD8cdTeERTsQfxNowClgnLJDJRuusXE"
                      helperText="Kopirajte API key iz WordPress Admin → SejkanSync → Settings"
                      sx={{ mb: 2 }}
                    />

                    {pluginConfig.api_key && (
                      <>
                        <Alert severity="success" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            ✅ Plugin je konfigurisan. Full Sync će koristiti brzi plugin umjesto REST API-ja.
                          </Typography>
                        </Alert>

                        <Button
                          variant="outlined"
                          startIcon={loadingSyncLog ? <CircularProgress size={16} /> : <LogIcon />}
                          onClick={() => editingShop && fetchSyncLog(editingShop)}
                          disabled={loadingSyncLog}
                          sx={{ mt: 1 }}
                        >
                          Prikaži Sync Log (zadnja sesija)
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </Paper>

              {/* Upute za instalaciju */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  📦 Instalacija Plugina
                </Typography>
                <Typography variant="body2" component="div" sx={{ '& ol': { pl: 2, m: 0 }, '& li': { mb: 1 } }}>
                  <ol>
                    <li>Preuzmite <code>sejkan-sync</code> folder iz GitHub repozitorija</li>
                    <li>Upload na WordPress: <code>/wp-content/plugins/sejkan-sync/</code></li>
                    <li>Aktivirajte plugin u WordPress Admin → Plugins</li>
                    <li>Idite na SejkanSync → Settings i kopirajte API Key</li>
                    <li>Zalijepite API Key ovdje i sačuvajte</li>
                  </ol>
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Prednosti SejkanSync Plugina:
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { icon: '🚀', text: '5-10x brži sync' },
                    { icon: '⏱️', text: 'Nema timeout-a (Action Scheduler)' },
                    { icon: '📊', text: 'Real-time progress u WP Admin' },
                    { icon: '🔄', text: 'Background processing' },
                  ].map((item, idx) => (
                    <Grid item xs={6} key={idx}>
                      <Chip
                        label={`${item.icon} ${item.text}`}
                        variant="outlined"
                        sx={{ width: '100%', justifyContent: 'flex-start' }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          )}

          {/* TAB 9: Premium Features */}
          {settingsTab === 9 && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Premium funkcije</strong> su dodatne opcije koje možete omogućiti za ovaj shop.
                  Ove funkcije se naplaćuju posebno.
                </Typography>
              </Alert>

              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PremiumIcon sx={{ color: '#f59e0b' }} />
                  Premium Funkcije
                </Typography>

                {/* Google Image Search */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={premiumFeatures.enable_image_search}
                        onChange={(e) => setPremiumFeatures(prev => ({ ...prev, enable_image_search: e.target.checked }))}
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          🖼️ Google Image Search
                          <Chip label="PREMIUM" size="small" sx={{ bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontSize: '10px', height: '18px' }} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Automatski pronalazi slike za proizvode koji ih nemaju koristeći Google Custom Search API
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', m: 0 }}
                  />
                </Box>

                {/* Gemini AI Content Generation */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={premiumFeatures.enable_llm}
                        onChange={(e) => setPremiumFeatures(prev => ({ ...prev, enable_llm: e.target.checked }))}
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          🤖 Gemini AI Content Generation
                          <Chip label="PREMIUM" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontSize: '10px', height: '18px' }} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Koristi Google Gemini za automatsko generiranje SEO naslova i opisa proizvoda
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', m: 0 }}
                  />
                </Box>

                {/* Bulk Edit & Campaigns */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={premiumFeatures.enable_bulk_campaigns}
                        onChange={(e) => setPremiumFeatures(prev => ({ ...prev, enable_bulk_campaigns: e.target.checked }))}
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          🎯 Bulk Edit & Kampanje
                          <Chip label="PREMIUM" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontSize: '10px', height: '18px' }} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Omogućava bulk uređivanje cijena, kreiranje akcija/kampanja sa automatskim revert-om po isteku
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', m: 0 }}
                  />
                </Box>

                {/* OLX Integration */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={premiumFeatures.enable_olx}
                        onChange={(e) => setPremiumFeatures(prev => ({ ...prev, enable_olx: e.target.checked }))}
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          🛒 OLX.ba Integracija
                          <Chip label="PREMIUM" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontSize: '10px', height: '18px' }} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Objavljujte proizvode direktno na OLX.ba sa automatskim skraćivanjem naslova, prilagođavanjem slika i formatiranjem opisa
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', m: 0 }}
                  />
                </Box>

                {/* Omnibus Directive */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={premiumFeatures.enable_omnibus}
                        onChange={(e) => setPremiumFeatures(prev => ({ ...prev, enable_omnibus: e.target.checked }))}
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          📊 Omnibus Direktiva (Historija cijena)
                          <Chip label="PREMIUM" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontSize: '10px', height: '18px' }} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Automatsko praćenje historije cijena i prikaz najniže cijene u zadnjih 30 dana kada je proizvod na akciji (EU/BiH zakon)
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', m: 0 }}
                  />
                </Box>

                {/* Google review emails: approval belongs to the platform owner. */}
                <Box sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={premiumFeatures.enable_review_emails}
                        onChange={(e) => setPremiumFeatures(prev => ({ ...prev, enable_review_emails: e.target.checked }))}
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          ⭐ Google recenzije putem emaila
                          <Chip label="PREMIUM" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontSize: '10px', height: '18px' }} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Automatska personalizirana zahvala i poziv na Google recenziju nakon završene WooCommerce narudžbe.
                          Nakon odobrenja klijent podešava izgled emaila, direktni Google link i odgodu slanja u WordPress pluginu.
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', m: 0 }}
                  />
                </Box>

                {/* OLX AI replies: approval belongs to the platform owner. */}
                <Box sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={premiumFeatures.enable_olx_ai_replies}
                        onChange={(e) => setPremiumFeatures(prev => ({ ...prev, enable_olx_ai_replies: e.target.checked }))}
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          🤖 OLX AI odgovori na poruke
                          <Chip label="PREMIUM" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontSize: '10px', height: '18px' }} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          AI čita poruke kupaca na OLX-u i odgovara iz stanja i cijene u WooCommerceu i podataka o firmi (prijedlozi ili automatsko slanje).
                          Zahtijeva OLX integraciju i AI provider (OpenRouter) u pluginu.
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', m: 0 }}
                  />
                </Box>

                {/* Supabase Direct */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={premiumFeatures.enable_supabase_direct}
                        onChange={(e) => setPremiumFeatures(prev => ({ ...prev, enable_supabase_direct: e.target.checked }))}
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          🗄️ Supabase Direct (Izvorne Slike)
                          <Chip label="PREMIUM" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontSize: '10px', height: '18px' }} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Pregled i reimport originalnih slika iz Supabase baze direktno na product edit stranici u WooCommerce-u
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', m: 0 }}
                  />
                </Box>
              </Paper>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Napomena:</strong> Klijent mora konfigurisati svoje API ključeve u WordPress pluginu
                  (Google API Key za Image Search, Gemini API Key za AI generiranje).
                </Typography>
              </Alert>
            </Box>
          )}

          {/* TAB 10: Menu Order - Postavljanje menu_order po dobavljaču */}
          {settingsTab === 10 && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Postavite <strong>menu_order</strong> vrijednost za WooCommerce proizvode po dobavljaču.
                  Manji broj = veći prioritet u prikazu (npr. 1 = najvažniji, 10 = manje važan).
                </Typography>
              </Alert>

              <Paper sx={{ p: 3, mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncOptions.supplier_menu_order?.enabled ?? false}
                      onChange={(e) => setSyncOptions(prev => ({
                        ...prev,
                        supplier_menu_order: {
                          enabled: e.target.checked,
                          orders: prev.supplier_menu_order?.orders ?? {},
                          default_order: prev.supplier_menu_order?.default_order ?? 0,
                        },
                      }))}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Aktiviraj Menu Order po dobavljaču
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Kada je uključeno, proizvodi će dobiti menu_order vrijednost prema dobavljaču prilikom sync-a
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              {syncOptions.supplier_menu_order?.enabled && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Menu Order po dobavljaču
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {availableSuppliers.map((supplier) => (
                      <Grid item xs={12} sm={6} md={4} key={supplier}>
                        <Paper
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: (syncOptions.supplier_menu_order?.orders?.[supplier] !== undefined) ? '#22c55e' : '#e2e8f0',
                            bgcolor: (syncOptions.supplier_menu_order?.orders?.[supplier] !== undefined) ? '#f0fdf4' : 'white',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1e293b' }}>
                            {supplier}
                          </Typography>
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            placeholder="Menu Order"
                            value={syncOptions.supplier_menu_order?.orders?.[supplier] ?? ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSyncOptions(prev => {
                                const newOrders = { ...(prev.supplier_menu_order?.orders ?? {}) };
                                if (value === '' || value === null) {
                                  delete newOrders[supplier];
                                } else {
                                  newOrders[supplier] = parseInt(value) || 0;
                                }
                                return {
                                  ...prev,
                                  supplier_menu_order: {
                                    enabled: prev.supplier_menu_order?.enabled ?? false,
                                    orders: newOrders,
                                    default_order: prev.supplier_menu_order?.default_order ?? 0,
                                  },
                                };
                              });
                            }}
                            inputProps={{ min: 0, step: 1 }}
                            helperText={
                              (syncOptions.supplier_menu_order?.orders?.[supplier] !== undefined)
                                ? `Postavljeno: ${syncOptions.supplier_menu_order?.orders?.[supplier]}`
                                : 'Koristit će default'
                            }
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Default Menu Order (za dobavljače koji nisu na listi):
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={syncOptions.supplier_menu_order?.default_order ?? 0}
                      onChange={(e) => setSyncOptions(prev => ({
                        ...prev,
                        supplier_menu_order: {
                          enabled: prev.supplier_menu_order?.enabled ?? false,
                          orders: prev.supplier_menu_order?.orders ?? {},
                          default_order: parseInt(e.target.value) || 0,
                        },
                      }))}
                      inputProps={{ min: 0, step: 1 }}
                      sx={{ width: 120 }}
                    />
                  </Box>

                  <Alert severity="success">
                    <Typography variant="body2">
                      <strong>Aktivni dobavljači:</strong>{' '}
                      {Object.entries(syncOptions.supplier_menu_order?.orders ?? {})
                        .sort((a, b) => a[1] - b[1])
                        .map(([s, o]) => `${s}=${o}`)
                        .join(', ') || 'Nema postavljenih'}
                    </Typography>
                  </Alert>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>
            Odustani
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={saveSettings}
            disabled={saving}
          >
            Sačuvaj
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================================
          SYNC PROGRESS DIALOG
          ===================================================================== */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => !syncing[syncingShop?.id || ''] && setSyncDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon color="primary" />
          Sync: {syncingShop?.name}
        </DialogTitle>
        <DialogContent>
          {/* Progress */}
          {syncProgress && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                {syncProgress.status === 'starting' || syncProgress.status === 'running' ? (
                  <CircularProgress size={24} />
                ) : syncProgress.status === 'completed' ? (
                  <SuccessIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
                <Typography>{syncProgress.message}</Typography>
              </Box>
              {(syncProgress.status === 'starting' || syncProgress.status === 'running') && (
                <LinearProgress />
              )}
            </Box>
          )}

          {/* Result */}
          {syncResult && !syncResult.error && syncResult.results?.[0] && (
            <Box>
              <Alert
                severity={syncResult.results[0].errors > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                Sinhronizacija završena!
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  icon={<SuccessIcon />}
                  label={`Kreirano: ${syncResult.results[0].created}`}
                  color="success"
                />
                <Chip
                  icon={<SyncIcon />}
                  label={`Ažurirano: ${syncResult.results[0].updated}`}
                  color="primary"
                />
                <Chip
                  label={`Preskočeno: ${syncResult.results[0].skipped}`}
                  variant="outlined"
                />
                {(syncResult.results[0] as any).deleted > 0 && (
                  <Chip
                    icon={<DeleteIcon />}
                    label={`Za brisanje: ${(syncResult.results[0] as any).deleted}`}
                    color="warning"
                  />
                )}
                {syncResult.results[0].errors > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`Greške: ${syncResult.results[0].errors}`}
                    color="error"
                  />
                )}
              </Box>

              {/* Logs */}
              {syncResult.results[0].logs && syncResult.results[0].logs.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Log (zadnjih 20):</Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1,
                      maxHeight: 200,
                      overflow: 'auto',
                      bgcolor: '#f5f5f5',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                    }}
                  >
                    {syncResult.results[0].logs.slice(-20).map((log: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          py: 0.25,
                          color: log.level === 'error' ? 'error.main' :
                                 log.level === 'success' ? 'success.main' :
                                 'text.primary'
                        }}
                      >
                        [{log.level}] {log.message}
                      </Box>
                    ))}
                  </Paper>
                </Box>
              )}
            </Box>
          )}

          {/* Error */}
          {syncResult?.error && (
            <Alert severity="error">
              {syncResult.error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSyncDialogOpen(false)}
            disabled={syncing[syncingShop?.id || '']}
          >
            Zatvori
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================================
          LIVE PREVIEW DIALOG
          ===================================================================== */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PreviewIcon color="primary" />
            Live Mapping Preview: {previewShop?.name}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Unesi SKU..."
              value={previewSkuInput}
              onChange={(e) => setPreviewSkuInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && previewShop && previewSkuInput.trim()) {
                  loadProductBySkuForPreview(previewShop, previewSkuInput.trim());
                }
              }}
              sx={{ width: 180 }}
              disabled={loadingPreview}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => previewShop && previewSkuInput.trim() && loadProductBySkuForPreview(previewShop, previewSkuInput.trim())}
              disabled={loadingPreview || !previewSkuInput.trim()}
            >
              Traži
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={loadingPreview ? <CircularProgress size={16} /> : <RandomIcon />}
              onClick={() => previewShop && loadRandomProductForPreview(previewShop)}
              disabled={loadingPreview}
            >
              Random
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : previewProduct && previewMappedProduct ? (
            <Grid container spacing={3}>
              {/* LIJEVA STRANA: Supabase */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: '#e3f2fd',
                    borderColor: 'primary.main',
                    borderWidth: 2,
                    height: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <DatabaseIcon color="primary" />
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                      SUPABASE (Source)
                    </Typography>
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: 140 }}>Naziv</TableCell>
                          <TableCell>{previewProduct.name || previewProduct.naziv || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                          <TableCell>{previewProduct.sku || previewProduct.sifra || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Nabavna cijena</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`${previewProduct.nabavna_cijena || previewProduct.regular_price || '0'} KM`}
                              color="warning"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Stock</TableCell>
                          <TableCell>{previewProduct.stock_quantity || previewProduct.kolicina || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Dobavljač</TableCell>
                          <TableCell>{previewProduct.dobavljac || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Kategorije</TableCell>
                          <TableCell>
                            {(() => {
                              let cats = previewProduct.categories || '';
                              if (typeof cats === 'string') {
                                return cats || '-';
                              }
                              if (Array.isArray(cats)) {
                                return cats.map((c: any) => typeof c === 'string' ? c : c?.name).join(', ') || '-';
                              }
                              return '-';
                            })()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Specifikacije</TableCell>
                          <TableCell>
                            {previewProduct.specifikacije ? (
                              <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                                {Object.entries(previewProduct.specifikacije).slice(0, 8).map(([key, val]) => (
                                  <Box key={key} sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                    <strong>{key}:</strong> {String(val).substring(0, 50)}
                                  </Box>
                                ))}
                                {Object.keys(previewProduct.specifikacije).length > 8 && (
                                  <Typography variant="caption" color="text.secondary">
                                    ... i još {Object.keys(previewProduct.specifikacije).length - 8}
                                  </Typography>
                                )}
                              </Box>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Slike</TableCell>
                          <TableCell>
                            {(() => {
                              let imgs = previewProduct.images || [];
                              if (typeof imgs === 'string') {
                                imgs = imgs.split('|').filter(Boolean);
                              }
                              return `${Array.isArray(imgs) ? imgs.length : 0} slika`;
                            })()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* DESNA STRANA: WooCommerce */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: previewMappedProduct._isExisting ? '#fff3e0' : '#e8f5e9',
                    borderColor: previewMappedProduct._isExisting ? 'warning.main' : 'success.main',
                    borderWidth: 2,
                    height: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WooIcon color={previewMappedProduct._isExisting ? 'warning' : 'success'} />
                      <Typography variant="h6" color={previewMappedProduct._isExisting ? 'warning.main' : 'success.main'} sx={{ fontWeight: 600 }}>
                        WOOCOMMERCE (Target)
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={previewMappedProduct._isExisting ? `UPDATE (ID: ${previewMappedProduct._targetProductId})` : 'CREATE'}
                      color={previewMappedProduct._isExisting ? 'warning' : 'success'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: 140 }}>Naziv</TableCell>
                          <TableCell>
                            {previewMappedProduct.name || '(prazan)'}
                            {previewMappedProduct._nameModified && (
                              <Chip size="small" label="Modificirano" color="info" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                          <TableCell>{previewMappedProduct.sku || '(prazan)'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Regular cijena</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`${previewMappedProduct.regular_price || '0'} KM`}
                              color="success"
                            />
                            {previewMappedProduct._price_calculation && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {previewMappedProduct._price_calculation}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        {previewMappedProduct.sale_price && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Akcijska cijena</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={`${previewMappedProduct.sale_price} KM`}
                                color="error"
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Stock</TableCell>
                          <TableCell>
                            {previewMappedProduct.stock_quantity || 0}
                            <Chip
                              size="small"
                              label={previewMappedProduct.stock_status}
                              color={previewMappedProduct.stock_status === 'instock' ? 'success' : 'error'}
                              sx={{ ml: 1 }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={previewMappedProduct.status || 'draft'}
                              color={previewMappedProduct.status === 'publish' ? 'success' : 'default'}
                            />
                            {previewMappedProduct._statusNote && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({previewMappedProduct._statusNote})
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Kategorije</TableCell>
                          <TableCell>
                            {previewMappedProduct.categories?.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {previewMappedProduct.categories.map((cat: any, idx: number) => (
                                  <Chip
                                    key={idx}
                                    size="small"
                                    label={cat.id ? `${cat.name} (ID: ${cat.id})` : cat.name}
                                    color={cat._mapped ? 'success' : 'warning'}
                                    icon={cat._mapped ? <MappedIcon /> : <UnmappedIcon />}
                                  />
                                ))}
                              </Box>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Atributi</TableCell>
                          <TableCell>
                            {previewMappedProduct.attributes?.length > 0 ? (
                              <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                                {previewMappedProduct.attributes.map((attr: any, idx: number) => (
                                  <Box key={idx} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {attr._mapped ? (
                                      <MappedIcon fontSize="small" color="success" />
                                    ) : (
                                      <UnmappedIcon fontSize="small" color="warning" />
                                    )}
                                    <Typography variant="body2">
                                      <strong>{attr.name}</strong>: {attr.options?.[0] || '-'}
                                      {attr._source && attr._mapped && (
                                        <Typography component="span" variant="caption" color="text.secondary">
                                          {' '}(iz: {attr._source})
                                        </Typography>
                                      )}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Slike</TableCell>
                          <TableCell>
                            {previewMappedProduct.images?.length || 0} slika
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <PreviewIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                Klikni "Random proizvod" za učitavanje primjera
              </Typography>
            </Box>
          )}

          {/* Legenda */}
          {previewProduct && previewMappedProduct && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Legenda:</Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MappedIcon color="success" fontSize="small" />
                  <Typography variant="body2">Mapirano (ima definiran ID)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <UnmappedIcon color="warning" fontSize="small" />
                  <Typography variant="body2">Nemapirano (koristi originalni naziv)</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button onClick={() => setPreviewDialogOpen(false)} color="inherit">
            Zatvori
          </Button>
          {previewProduct && previewMappedProduct && (
            <Button
              variant="contained"
              color={previewMappedProduct._isExisting ? 'warning' : 'success'}
              onClick={handleSyncPreviewProduct}
              disabled={syncingPreview}
              startIcon={syncingPreview ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {syncingPreview
                ? 'Sinkronizacija...'
                : previewMappedProduct._isExisting
                  ? `AŽURIRAJ (ID: ${previewMappedProduct._targetProductId})`
                  : 'KREIRAJ PROIZVOD'
              }
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* =====================================================================
          ADD SHOP DIALOG
          ===================================================================== */}
      <Dialog
        open={addShopDialogOpen}
        onClose={() => {
          setAddShopDialogOpen(false);
          setNewShopData({ name: '', url: '', consumer_key: '', consumer_secret: '', plugin_enabled: false, plugin_api_key: '' });
          setConnectionTestResult(null);
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon color="success" />
          Dodaj novi WooCommerce shop
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Naziv shopa"
              placeholder="npr. Moj Web Shop"
              value={newShopData.name}
              onChange={(e) => setNewShopData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="URL"
              placeholder="https://mojshop.ba"
              value={newShopData.url}
              onChange={(e) => setNewShopData(prev => ({ ...prev, url: e.target.value }))}
              fullWidth
              required
              helperText="API URL vašeg WooCommerce shopa"
            />
            <TextField
              label="Consumer Key"
              placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={newShopData.consumer_key}
              onChange={(e) => setNewShopData(prev => ({ ...prev, consumer_key: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Consumer Secret"
              placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={newShopData.consumer_secret}
              onChange={(e) => setNewShopData(prev => ({ ...prev, consumer_secret: e.target.value }))}
              fullWidth
              required
              type="password"
            />

            {/* SejkanSync Plugin Config */}
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569' }}>
              SejkanSync Plugin (opciono)
            </Typography>
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                Ako imate instaliran SejkanSync plugin na WordPress-u, unesite API key za 5-10x brži sync.
              </Typography>
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={newShopData.plugin_enabled}
                  onChange={(e) => setNewShopData(prev => ({ ...prev, plugin_enabled: e.target.checked }))}
                  color="success"
                />
              }
              label="Koristi SejkanSync plugin"
            />
            {newShopData.plugin_enabled && (
              <TextField
                label="Plugin API Key"
                placeholder="zD8cdTeERTsQfxNowClgnLJDJRuusXE"
                value={newShopData.plugin_api_key}
                onChange={(e) => setNewShopData(prev => ({ ...prev, plugin_api_key: e.target.value }))}
                fullWidth
                helperText="Kopirajte API key iz WordPress Admin → SejkanSync → Settings"
              />
            )}

            {/* Connection Test Result */}
            {connectionTestResult && (
              <Alert severity={connectionTestResult.success ? 'success' : 'error'}>
                {connectionTestResult.message}
              </Alert>
            )}

            <Button
              variant="outlined"
              onClick={testNewShopConnection}
              disabled={testingConnection || !newShopData.url || !newShopData.consumer_key || !newShopData.consumer_secret}
              startIcon={testingConnection ? <CircularProgress size={20} /> : <SyncIcon />}
            >
              {testingConnection ? 'Testiram...' : 'Testiraj konekciju'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddShopDialogOpen(false);
              setNewShopData({ name: '', url: '', consumer_key: '', consumer_secret: '', plugin_enabled: false, plugin_api_key: '' });
              setConnectionTestResult(null);
            }}
          >
            Odustani
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={addNewShop}
            disabled={addingShop || !newShopData.name || !newShopData.url || !newShopData.consumer_key || !newShopData.consumer_secret}
            startIcon={addingShop ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {addingShop ? 'Spremam...' : 'Dodaj Shop'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================================
          DELETE SHOP DIALOG
          ===================================================================== */}
      <Dialog
        open={deleteShopDialogOpen}
        onClose={() => {
          setDeleteShopDialogOpen(false);
          setShopToDelete(null);
        }}
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <DeleteIcon color="error" />
          Potvrda brisanja
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ova akcija je nepovratna!
          </Alert>
          <Typography>
            Da li ste sigurni da želite obrisati shop <strong>"{shopToDelete?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            URL: {shopToDelete?.url}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteShopDialogOpen(false);
              setShopToDelete(null);
            }}
          >
            Odustani
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteShop}
            disabled={deletingShop}
            startIcon={deletingShop ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deletingShop ? 'Brišem...' : 'Obriši Shop'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================================
          SYNC LOG DIALOG
          ===================================================================== */}
      <Dialog
        open={syncLogDialogOpen}
        onClose={() => setSyncLogDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LogIcon color="primary" />
          Sync Log - Zadnja Sesija
          {syncLogData && (
            <Chip
              label={syncLogData.batch_status}
              color={syncLogData.batch_status === 'completed' ? 'success' : 'warning'}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loadingSyncLog && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {syncLogData && !loadingSyncLog && (
            <>
              {/* Stats summary */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="text.secondary">Ukupno</Typography>
                    <Typography variant="h6">{syncLogData.stats.total}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="success.main">Uspješno</Typography>
                    <Typography variant="h6" color="success.main">{syncLogData.stats.success}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="error.main">Neuspješno</Typography>
                    <Typography variant="h6" color="error.main">{syncLogData.stats.failed}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="info.main">Kreirano</Typography>
                    <Typography variant="h6" color="info.main">{syncLogData.stats.created}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="primary.main">Ažurirano</Typography>
                    <Typography variant="h6" color="primary.main">{syncLogData.stats.updated}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="text.secondary">Vrijeme</Typography>
                    <Typography variant="body2">
                      {new Date(syncLogData.batch_created_at).toLocaleString('hr-HR')}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Filter */}
              <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                <Chip
                  label="Svi"
                  variant={syncLogFilter === 'all' ? 'filled' : 'outlined'}
                  onClick={() => setSyncLogFilter('all')}
                />
                <Chip
                  label="Uspješni"
                  color="success"
                  variant={syncLogFilter === 'success' ? 'filled' : 'outlined'}
                  onClick={() => setSyncLogFilter('success')}
                />
                <Chip
                  label="Neuspješni"
                  color="error"
                  variant={syncLogFilter === 'failed' ? 'filled' : 'outlined'}
                  onClick={() => setSyncLogFilter('failed')}
                />
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => editingShop && fetchSyncLog(editingShop)}
                  sx={{ ml: 'auto' }}
                >
                  Osvježi
                </Button>
              </Box>

              {/* Log table */}
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>Akcija</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Promjene</TableCell>
                      <TableCell>Greška</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncLogData.logs
                      .filter(log => syncLogFilter === 'all' || log.status === syncLogFilter)
                      .map((log, idx) => (
                        <TableRow key={idx} sx={{ backgroundColor: log.status === 'failed' ? 'error.light' : 'inherit' }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                              {log.sku}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.action}
                              size="small"
                              color={log.action === 'created' ? 'info' : log.action === 'updated' ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {log.status === 'success' ? (
                              <SuccessIcon color="success" fontSize="small" />
                            ) : (
                              <ErrorIcon color="error" fontSize="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {log.changes && (
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {Object.entries(log.changes)
                                  .filter(([k]) => k !== 'meta')
                                  .map(([k, v]) => `${k}=${v}`)
                                  .join(', ')}
                                {log.changes.meta && Object.keys(log.changes.meta).length > 0 && (
                                  <span style={{ color: '#666' }}>
                                    {' | meta: '}
                                    {Object.entries(log.changes.meta as Record<string, any>)
                                      .map(([k, v]) => `${k}=${v}`)
                                      .join(', ')}
                                  </span>
                                )}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {log.error_message && (
                              <Typography variant="caption" color="error">
                                {log.error_message}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {syncLogData.logs.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Nema log zapisa za ovu sesiju. Možda plugin nije još završio procesiranje?
                </Alert>
              )}
            </>
          )}

          {!syncLogData && !loadingSyncLog && (
            <Alert severity="warning">
              Nije pronađen nijedan sync log. Provjerite da li je plugin ispravno konfigurisan i da je sync bar jednom pokrenut.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncLogDialogOpen(false)}>
            Zatvori
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
